

// ═══════════════════════════════════════════
//  WEDOO INTERIORS — COMPLETE ADMIN PANEL
// ═══════════════════════════════════════════
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import api from '../utils/api';
import logoAdmin from '../assets/logo-admin.png';
import quoteLogo from '../assets/logo.png';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { sanitizePhone, isValidOptionalPhone, PHONE_ERROR, phoneInputProps } from '../utils/phone';
import { resolveTheme, rgb } from '../print/docThemes';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api$/,'') || '';
export const imgSrc = (p) => !p ? '' : /^https?:\/\//i.test(p) ? p : `${API_BASE}${p}`;

// Page header for every admin screen. Styled in index.css (.admin-head) so the
// 17 screens using it share one definition and stay in step with the document
// preview pages, which use the same treatment.
const AdminHeading = ({ eyebrow, title, action }) => (
  <div className="admin-head">
    <div>
      {eyebrow && <p className="admin-eyebrow">{eyebrow}</p>}
      <h1 className="admin-title">{title}</h1>
    </div>
    {action && <div className="admin-head-actions">{action}</div>}
  </div>
);

// Eye toggle used by password fields — inline SVG, no icon library needed.
const EyeToggle = ({ shown, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={shown ? 'Hide password' : 'Show password'}
    title={shown ? 'Hide password' : 'Show password'}
    style={{ position:'absolute', top:0, right:0, height:'100%', width:'3rem', display:'flex', alignItems:'center', justifyContent:'center', background:'none', border:'none', padding:0, cursor:'pointer', color:shown ? '#F5A800' : '#8a8278', transition:'color 0.2s' }}
  >
    {shown ? (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </svg>
    ) : (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    )}
  </button>
);

// ─── ADMIN LOGIN ──────────────────────────────────────────────────────────────
export function AdminLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  useEffect(() => { if (user) navigate('/admin'); }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await login(email, password); toast.success('Welcome back!'); navigate('/admin'); }
    catch (err) { toast.error(err.response?.data?.message || 'Invalid credentials'); }
    finally { setLoading(false); }
  };

  return (
    <div style={{ minHeight:'100vh', background:'#090909', display:'flex', alignItems:'center', justifyContent:'center', padding:'1.5rem', backgroundImage:'radial-gradient(ellipse at 20% 50%, rgba(245,168,0,0.04) 0%, transparent 60%)' }}>
      <div style={{ width:'100%', maxWidth:'420px' }}>
        <div style={{ textAlign:'center', marginBottom:'3rem' }}>
          <img src={logoAdmin} alt="Wedoo Interiors" style={{ height:'44px', width:'auto', margin:'0 auto 1.5rem', display:'block' }} />
          <h1 style={{ fontFamily:'var(--font-display)', fontSize:'1.8rem', fontWeight:600, color:'#f8f5ef' }}>Admin Panel</h1>
          <p style={{ color:'#8a8278', fontSize:'0.78rem', marginTop:'0.4rem', letterSpacing:'0.08em' }}>Wedoo Interiors Dashboard</p>
        </div>
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2.5rem' }}>
          <form onSubmit={handleSubmit}>
            <div className="form-group"><label>Email Address</label><input type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="admin@wedoointeriors.com" required autoFocus /></div>
            <div className="form-group">
              <label>Password</label>
              <div style={{ position:'relative' }}>
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" required style={{ paddingRight:'3rem' }} />
                <EyeToggle shown={showPassword} onToggle={()=>setShowPassword(v=>!v)} />
              </div>
            </div>
            <button type="submit" className="btn btn-gold" style={{ width:'100%', justifyContent:'center', marginTop:'0.5rem' }} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign In →'}
            </button>
          </form>
          <p style={{ textAlign:'center', color:'#8a8278', fontSize:'0.72rem', marginTop:'1.5rem' }}>
            First time? <Link to="/admin/setup" style={{ color:'#F5A800' }}>Set up admin account</Link>
          </p>
        </div>
        <p style={{ textAlign:'center', marginTop:'1.5rem' }}>
          <Link to="/" style={{ color:'#F5A800', fontSize:'0.75rem', letterSpacing:'0.08em' }}>← Back to Website</Link>
        </p>
      </div>
    </div>
  );
}

// ─── ADMIN LAYOUT ─────────────────────────────────────────────────────────────
const NAV = [
  { path:'/admin',              label:'Dashboard',    icon:'📊', exact:true },
  { divider: '── CRM ──' },
  { path:'/admin/crm/leads',     label:'All Leads',     icon:'📋' },
  { path:'/admin/crm/pipeline',  label:'Pipeline',      icon:'🔄' },
  { path:'/admin/crm/reminders', label:'Reminders',     icon:'🔔' },
  { path:'/admin/crm/analytics', label:'Analytics',     icon:'📈' },
  { divider: '── Content ──' },
  { path:'/admin/banners',      label:'Banners',       icon:'🖼️' },
  { path:'/admin/gallery',      label:'Gallery',       icon:'🖼️' },
  { path:'/admin/offers',      label:'Offers',       icon:'🖼️' },
  { path:'/admin/portfolio',    label:'Portfolio',     icon:'🏗️' },
  { path:'/admin/blog',         label:'Blog',          icon:'✍️' },
  { path:'/admin/reviews',      label:'Reviews',       icon:'⭐' },
  { path:'/admin/faq',          label:'FAQ',           icon:'❓' },
  { divider: '── Operations ──' },
  { path:'/admin/careers',      label:'Careers',       icon:'💼' },
  { path:'/admin/contacts',     label:'Contacts',      icon:'📩' },
  { path:'/admin/quotation',    label:'Quotations',    icon:'📄', children:[
      { path:'/admin/quotation',              label:'All Quotations' },
    ] },
  { path:'/admin/invoice',      label:'Invoice',       icon:'🧾' },
  { path:'/admin/social',       label:'Social Media',  icon:'📱' },
  { path:'/admin/testimonials', label:'Testimonials',  icon:'🌟' },
  { path:'/admin/seo',          label:'SEO',           icon:'🔍' },
  { path:'/admin/users',        label:'Users',         icon:'👥' },
  { path:'/admin/maintenance',  label:'Maintenance',   icon:'🛠️', children:[
      { path:'/admin/maintenance/item', label:'Item' },
      { path:'/admin/maintenance/material', label:'Material' },
    ] },
  { path:'/admin/settings',     label:'Settings',      icon:'⚙️' },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set(NAV.filter(n=>n.children && location.pathname.startsWith(n.path)).map(n=>n.label)));

  const isActive = (n) => n.exact ? location.pathname === n.path : location.pathname.startsWith(n.path);
  const toggleExpand = (label) => setExpanded(prev => { const next = new Set(prev); next.has(label) ? next.delete(label) : next.add(label); return next; });
  const handleLogout = () => { logout(); navigate('/admin/login'); };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:'#0c0c0c' }}>
      {/* Overlay */}
      {open && <div onClick={()=>setOpen(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:98 }} className="mob-overlay" />}

      {/* Sidebar */}
      <aside style={{ width:'230px', flexShrink:0, background:'#090909', borderRight:'1px solid rgba(245,168,0,0.1)', display:'flex', flexDirection:'column', height:'100vh', position:'fixed', top:0, left:0, zIndex:99, transform: open ? 'translateX(0)' : undefined }} className="admin-sidebar">
        <div style={{ padding:'1.5rem 1.25rem', borderBottom:'1px solid rgba(245,168,0,0.1)', display:'flex', alignItems:'center', gap:'0.75rem' }}>
          <div style={{ width:'36px', height:'36px', background:'linear-gradient(135deg,#F5A800,#FFB800)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', fontWeight:700, fontSize:'1.1rem', color:'#0c0c0c', flexShrink:0 }}>W</div>
          <div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'0.95rem', color:'#f8f5ef' }}>Wedoo Interiors</div>
            <div style={{ fontSize:'0.55rem', letterSpacing:'0.18em', textTransform:'uppercase', color:'#F5A800' }}>Admin</div>
          </div>
        </div>
        <nav style={{ flex:1, padding:'0.75rem 0', overflowY:'auto' }}>
          {NAV.map((n, i) => n.divider ? (
            <div key={i} style={{ padding:'0.6rem 1.25rem 0.25rem', fontSize:'0.58rem', letterSpacing:'0.18em', color:'rgba(138,130,120,0.5)', textTransform:'uppercase', fontWeight:'500' }}>{n.divider}</div>
          ) : n.children ? (
            <div key={n.path}>
              <button onClick={()=>toggleExpand(n.label)} style={{ display:'flex', alignItems:'center', width:'100%', gap:'0.75rem', padding:'0.65rem 1.25rem', fontSize:'0.78rem', fontWeight:400, letterSpacing:'0.04em', transition:'all 0.2s', background: isActive(n) ? 'rgba(245,168,0,0.07)' : 'transparent', color: isActive(n) ? '#F5A800' : '#8a8278', borderLeft: isActive(n) ? '2px solid #F5A800' : '2px solid transparent', cursor:'pointer' }}>
                <span style={{ fontSize:'0.9rem', width:'18px', textAlign:'center' }}>{n.icon}</span> {n.label}
                <span style={{ marginLeft:'auto', fontSize:'0.65rem', transition:'transform 0.2s', transform: expanded.has(n.label) ? 'rotate(90deg)' : 'rotate(0deg)' }}>›</span>
              </button>
              {expanded.has(n.label) && n.children.map((c,ci) => (
                <Link key={ci} to={c.path} onClick={()=>setOpen(false)} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.5rem 1.25rem 0.5rem 2.9rem', fontSize:'0.74rem', fontWeight:400, letterSpacing:'0.04em', color:'#8a8278', transition:'all 0.2s' }}
                  onMouseEnter={e=>{ e.currentTarget.style.color='#F5A800'; }} onMouseLeave={e=>{ e.currentTarget.style.color='#8a8278'; }}>
                  {c.label}
                </Link>
              ))}
            </div>
          ) : (
            <Link key={n.path} to={n.path} onClick={()=>setOpen(false)} style={{ display:'flex', alignItems:'center', gap:'0.75rem', padding:'0.65rem 1.25rem', fontSize:'0.78rem', fontWeight:400, letterSpacing:'0.04em', transition:'all 0.2s', color: isActive(n) ? '#F5A800' : '#8a8278', background: isActive(n) ? 'rgba(245,168,0,0.07)' : 'transparent', borderLeft: isActive(n) ? '2px solid #F5A800' : '2px solid transparent' }}>
              <span style={{ fontSize:'0.9rem', width:'18px', textAlign:'center' }}>{n.icon}</span> {n.label}
            </Link>
          ))}
        </nav>
        <div style={{ padding:'1rem 1.25rem', borderTop:'1px solid rgba(245,168,0,0.1)' }}>
          <div style={{ fontSize:'0.72rem', color:'#8a8278', marginBottom:'0.6rem', display:'flex', alignItems:'center', gap:'0.4rem' }}>
            <span style={{ color:'#4ade80', fontSize:'0.5rem' }}>●</span> {user?.name}
            <span className="badge badge-gold" style={{ fontSize:'0.55rem', marginLeft:'auto' }}>{user?.role}</span>
          </div>
          <button onClick={handleLogout} style={{ width:'100%', padding:'0.55rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', fontSize:'0.72rem', cursor:'pointer', letterSpacing:'0.08em' }}>Logout</button>
        </div>
      </aside>

      {/* Main */}
      <div style={{ flex:1, marginLeft:'0', display:'flex', flexDirection:'column' }} className="admin-main">
        <header style={{ position:'sticky', top:0, zIndex:50, background:'rgba(12,12,12,0.97)', backdropFilter:'blur(16px)', borderBottom:'1px solid rgba(245,168,0,0.1)', padding:'0.8rem 1.5rem', display:'flex', alignItems:'center', gap:'1rem' }}>
          <button onClick={()=>setOpen(!open)} style={{ color:'#8a8278', fontSize:'1.3rem', padding:'0.25rem' }}>☰</button>
          <div style={{ flex:1 }}>
            <span style={{ fontSize:'0.75rem', color:'#8a8278', letterSpacing:'0.06em' }}>
              {NAV.find(n=>isActive(n))?.label || 'Dashboard'}
            </span>
          </div>
          <a href="/" target="_blank" rel="noopener noreferrer" style={{ fontSize:'0.68rem', color:'#F5A800', letterSpacing:'0.1em' }}>View Site →</a>
        </header>
        <main style={{ flex:1, padding:'2rem 1.75rem', width:'100%' }}>
          <Outlet />
        </main>
      </div>

      <style>{`
        @media(min-width:960px){.admin-sidebar{transform:translateX(0)!important;position:sticky!important;}.admin-main{margin-left:0!important}.mob-overlay{display:none!important}}
        @media(max-width:959px){.admin-sidebar{transform:translateX(-100%)}.admin-main{}}
        @media(max-width:600px){.admin-main main{padding:1.25rem 1rem!important}}
      `}</style>
    </div>
  );
}

// ─── DASHBOARD ────────────────────────────────────────────────────────────────
export function AdminDashboard() {
  const [data, setData] = useState({ contacts:0, portfolio:0, careers:0, newC:0 });
  const [recent, setRecent] = useState({ contacts:[] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get('/contacts'),api.get('/portfolio/all'),api.get('/gallery/all'),api.get('/careers/all')])
      .then(([c,p,g,j]) => {
        setData({ contacts:c.data.length, portfolio:p.data.length, gallery:g.data.length, careers:j.data.length, newC:c.data.filter(x=>x.status==='new').length });
        setRecent({ contacts:c.data.slice(0,5) });
      }).catch(()=>{}).finally(()=>setLoading(false));
  },[]);

  const Stat = ({ to, icon, label, value, badge }) => (
    <Link to={to} style={{ textDecoration:'none' }}>
      <div style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', padding:'1.75rem', cursor:'pointer', transition:'all 0.35s', position:'relative', overflow:'hidden' }}
        onMouseEnter={e=>{ e.currentTarget.style.borderColor='rgba(245,168,0,0.25)'; e.currentTarget.style.transform='translateY(-3px)'; }}
        onMouseLeave={e=>{ e.currentTarget.style.borderColor='rgba(255,255,255,0.05)'; e.currentTarget.style.transform='translateY(0)'; }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.25rem' }}>
          <span style={{ fontSize:'1.5rem' }}>{icon}</span>
          {badge > 0 && <span className="badge badge-gold">{badge} new</span>}
        </div>
        <div style={{ fontFamily:'var(--font-display)', fontSize:'2.5rem', color:'#f8f5ef', fontWeight:600, lineHeight:1, marginBottom:'0.4rem' }}>{value}</div>
        <div style={{ fontSize:'0.66rem', letterSpacing:'0.16em', textTransform:'uppercase', color:'#8a8278' }}>{label}</div>
        <div style={{ position:'absolute', bottom:'-25px', right:'-25px', width:'90px', height:'90px', borderRadius:'50%', border:'1px solid rgba(245,168,0,0.06)' }} />
      </div>
    </Link>
  );

  return (
    <div>
      <AdminHeading eyebrow="Overview" title="Dashboard" />
      {loading ? <p style={{ color:'#8a8278', padding:'3rem', textAlign:'center' }}>Loading...</p> : (
        <>
          <div className="grid-3" style={{ marginBottom:'2.5rem' }}>
            <Stat to="/admin/contacts" icon="📩" label="Contact Messages" value={data.contacts} badge={data.newC} />
            <Stat to="/admin/portfolio" icon="🗄️" label="Portfolio Items" value={data.portfolio} />
            <Stat to="/admin/careers" icon="💼" label="Job Openings" value={data.careers} />
          </div>
          <div style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', padding:'1.75rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem' }}>
                <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.2rem', fontWeight:400, color:'#f8f5ef' }}>Recent Contacts</h3>
                <Link to="/admin/contacts" style={{ fontSize:'0.68rem', color:'#F5A800', letterSpacing:'0.1em' }}>View All →</Link>
              </div>
              {recent.contacts.length===0 ? <p style={{ color:'#8a8278', fontSize:'0.82rem' }}>No contacts yet.</p> :
                recent.contacts.map(c => (
                  <div key={c._id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.7rem 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                    <div>
                      <p style={{ fontSize:'0.84rem', color:'#ede8de', fontWeight:500 }}>{c.name}</p>
                      <p style={{ fontSize:'0.72rem', color:'#8a8278' }}>{c.email}</p>
                    </div>
                    <span className={`badge ${c.status==='new'?'badge-gold':'badge-muted'}`}>{c.status}</span>
                  </div>
                ))
              }
            </div>
          <div style={{ marginTop:'2rem', background:'#141414', border:'1px solid rgba(245,168,0,0.12)', borderRadius:'var(--radius)', padding:'1.5rem' }}>
            <p style={{ fontSize:'0.7rem', letterSpacing:'0.15em', textTransform:'uppercase', color:'#F5A800', marginBottom:'1rem' }}>Quick Actions</p>
            <div style={{ display:'flex', gap:'0.6rem', flexWrap:'wrap' }}>
              {[['+ Add Banner','/admin/banners'],['+ Add Project','/admin/portfolio'],['+ Post Job','/admin/careers'],['+ Social Post','/admin/social'],['⚙ SEO','/admin/seo'],['⭐ Testimonials','/admin/testimonials']].map(([l,p])=>(
                <Link key={p} to={p} className="btn btn-dark" style={{ padding:'0.45rem 1rem', fontSize:'0.7rem' }}>{l}</Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── BANNERS ──────────────────────────────────────────────────────────────────
export function AdminBanners() {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title:'', subtitle:'', description:'', buttonText:'View Project', buttonLink:'', order:0 });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/banners/all').then(r=>setBanners(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!file) { toast.error('Please select an image'); return undefined; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v])=>fd.append(k,v));
      fd.append('image', file);
      await api.post('/banners', fd, { headers:{'Content-Type':'multipart/form-data'} });
      toast.success('Banner added!'); setShowForm(false); setFile(null); setForm({ title:'', subtitle:'', description:'', buttonText:'View Project', buttonLink:'', order:0 }); load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const del = async (id) => { if (!window.confirm('Delete?')) return undefined; await api.delete(`/banners/${id}`); toast.success('Deleted'); load(); };
  const toggle = async (b) => { await api.patch(`/banners/${b._id}`, { isActive:!b.isActive }); load(); };

  return (
    <div>
      <AdminHeading eyebrow="Hero Slider" title="Banner Management" action={<button className="btn btn-gold" onClick={()=>setShowForm(!showForm)} style={{ fontSize:'0.72rem' }}>{showForm?'✕ Cancel':'+ Add Banner'}</button>} />
      {showForm && (
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', marginBottom:'2rem' }}>
          <form onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group"><label>Slide Title *</label><input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} required placeholder="e.g. Spaces That Inspire" /></div>
              <div className="form-group"><label>Italic Highlight</label><input value={form.subtitle} onChange={e=>setForm(p=>({...p,subtitle:e.target.value}))} placeholder="e.g. Inspire" /></div>
              <div className="form-group"><label>Button Text</label><input value={form.buttonText} onChange={e=>setForm(p=>({...p,buttonText:e.target.value}))} /></div>
              <div className="form-group"><label>Button Link</label><input value={form.buttonLink} onChange={e=>setForm(p=>({...p,buttonLink:e.target.value}))} placeholder="/portfolio" /></div>
              <div className="form-group"><label>Order</label><input type="number" value={form.order} onChange={e=>setForm(p=>({...p,order:e.target.value}))} /></div>
            </div>
            <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} rows={2} placeholder="Slide sub-text..." /></div>
            <div className="form-group"><label>Banner Image * (1920×1080 recommended)</label><input type="file" accept="image/*" onChange={e=>setFile(e.target.files[0])} required style={{ color:'#8a8278', padding:'0.5rem 0' }} /></div>
            <div style={{ display:'flex', gap:'0.75rem' }}>
              <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':'Save Banner'}</button>
              <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={()=>setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {banners.length===0 && <p style={{ color:'#8a8278', textAlign:'center', padding:'4rem' }}>No banners yet. Add your first hero banner above.</p>}
          {banners.map(b => (
            <div key={b._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', display:'flex', gap:'1.25rem', alignItems:'center', padding:'1rem 1.25rem', flexWrap:'wrap' }}>
              <img src={imgSrc(b.image)} alt={b.title} style={{ width:'110px', height:'65px', objectFit:'cover', flexShrink:0 }} />
              <div style={{ flex:1, minWidth:'160px' }}>
                <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', marginBottom:'0.3rem' }}>
                  <h4 style={{ fontSize:'0.9rem', color:'#f8f5ef', fontFamily:'var(--font-display)', fontWeight:400 }}>{b.title}</h4>
                  <span className={`badge ${b.isActive?'badge-green':'badge-red'}`}>{b.isActive?'Active':'Hidden'}</span>
                  <span className="badge badge-muted">#{b.order}</span>
                </div>
                {b.description && <p style={{ color:'#8a8278', fontSize:'0.75rem' }}>{b.description}</p>}
              </div>
              <div style={{ display:'flex', gap:'0.5rem', flexShrink:0 }}>
                <button className="btn btn-dark" style={{ padding:'0.35rem 0.75rem', fontSize:'0.68rem' }} onClick={()=>toggle(b)}>{b.isActive?'Hide':'Show'}</button>
                <button onClick={()=>del(b._id)} style={{ padding:'0.35rem 0.75rem', fontSize:'0.68rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── PORTFOLIO ────────────────────────────────────────────────────────────────
const CATS = ['living-room','bedroom','kitchen','bathroom','false-ceiling','commercial','complete-modular','office'];
export function AdminPortfolio() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title:'', category:'living-room', description:'', location:'', bhkType:'', area:'', tags:'', featured:false, isActive:true });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/portfolio/all').then(r=>setProjects(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!files.length) { toast.error('Add at least one image'); return undefined; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v])=>fd.append(k,v));
      files.forEach(f=>fd.append('images',f));
      await api.post('/portfolio', fd, { headers:{'Content-Type':'multipart/form-data'} });
      toast.success('Project added!'); setShowForm(false); setFiles([]); setForm({ title:'', category:'living-room', description:'', location:'', bhkType:'', area:'', tags:'', featured:false, isActive:true }); load();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const del = async (id) => { if (!window.confirm('Delete?')) return undefined; await api.delete(`/portfolio/${id}`); toast.success('Deleted'); load(); };
  const toggleFeat = async (p) => { await api.patch(`/portfolio/${p._id}`, { featured:!p.featured }); load(); };

  return (
    <div>
      <AdminHeading eyebrow="Work Portfolio" title="Portfolio Management" action={<button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>setShowForm(!showForm)}>{showForm?'✕ Cancel':'+ Add Project'}</button>} />
      {showForm && (
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', marginBottom:'2rem' }}>
          <form onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group"><label>Project Title *</label><input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} required placeholder="e.g. Modern Living Room" /></div>
              <div className="form-group"><label>Category *</label><select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>{CATS.map(c=><option key={c} value={c}>{c.replace(/-/g,' ')}</option>)}</select></div>
              <div className="form-group"><label>Location</label><input value={form.location} onChange={e=>setForm(p=>({...p,location:e.target.value}))} placeholder="e.g. Tumakuru" /></div>
              <div className="form-group"><label>BHK Type</label><input value={form.bhkType} onChange={e=>setForm(p=>({...p,bhkType:e.target.value}))} placeholder="e.g. 3 BHK" /></div>
              <div className="form-group"><label>Area</label><input value={form.area} onChange={e=>setForm(p=>({...p,area:e.target.value}))} placeholder="e.g. 1800 sqft" /></div>
              <div className="form-group"><label>Tags (comma-separated)</label><input value={form.tags} onChange={e=>setForm(p=>({...p,tags:e.target.value}))} placeholder="modern, luxury, minimal" /></div>
            </div>
            <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} rows={3} placeholder="Describe this project..." /></div>
            <div className="form-group">
              <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer', textTransform:'none', letterSpacing:'normal', fontSize:'0.82rem', color:'#ede8de' }}>
                <input type="checkbox" checked={form.featured} onChange={e=>setForm(p=>({...p,featured:e.target.checked}))} /> Mark as Featured (appears on homepage)
              </label>
            </div>
            <div className="form-group"><label>Project Images * (multiple)</label><input type="file" accept="image/*" multiple onChange={e=>setFiles([...e.target.files])} style={{ color:'#8a8278', padding:'0.5rem 0' }} />{files.length>0&&<p style={{ color:'#F5A800', fontSize:'0.72rem', marginTop:'0.3rem' }}>{files.length} image(s) selected</p>}</div>
            <div style={{ display:'flex', gap:'0.75rem' }}>
              <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':'Save Project'}</button>
              <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={()=>setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : (
        <div className="grid-3" style={{ gap:'1rem' }}>
          {projects.length===0 && <p style={{ gridColumn:'1/-1', textAlign:'center', padding:'4rem', color:'#8a8278' }}>No projects yet.</p>}
          {projects.map(p => (
            <div key={p._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', overflow:'hidden' }}>
              {p.images?.[0] && <img src={imgSrc(p.images[0])} alt={p.title} style={{ width:'100%', height:'180px', objectFit:'cover' }} />}
              <div style={{ padding:'1.1rem' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.5rem' }}>
                  <h4 style={{ fontFamily:'var(--font-display)', fontSize:'1rem', fontWeight:400, color:'#f8f5ef', flex:1, marginRight:'0.5rem' }}>{p.title}</h4>
                  {p.featured && <span style={{ color:'#F5A800', fontSize:'0.9rem' }}></span>}
                </div>
                <span className="badge badge-blue" style={{ marginBottom:'0.75rem', display:'inline-block', fontSize:'0.62rem' }}>{p.category?.replace(/-/g,' ')}</span>
                {p.location && <p style={{ color:'#8a8278', fontSize:'0.72rem', marginBottom:'0.75rem' }}>📍 {p.location}</p>}
                <div style={{ display:'flex', gap:'0.4rem' }}>
                 <button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>setShowForm(!showForm)}>{showForm?'✕ Cancel':'+ Add Image'}</button> 
                  <button onClick={()=>del(p._id)} style={{ flex:1, padding:'0.35rem', fontSize:'0.68rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Delete</button>
                </div>
                
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── GALLERY ───────────────────────────────────────────────────────────────

export function AdminGallery() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title:'', category:'living-room', description:'', active:true });
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/gallery/all').then(r=>setItems(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);

  const resetForm = () => { setForm({ title:'', category:'living-room', description:'', active:true }); setFiles([]); setEditingId(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editingId && !files.length) { toast.error('Add at least one image'); return undefined; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v])=>fd.append(k,v));
      files.forEach(f=>fd.append('images',f));
      if (editingId) {
        await api.patch(`/gallery/${editingId}`, fd, { headers:{'Content-Type':'multipart/form-data'} });
        toast.success('Gallery item updated!');
      } else {
        await api.post('/gallery', fd, { headers:{'Content-Type':'multipart/form-data'} });
        toast.success('Gallery item added!');
      }
      setShowForm(false); resetForm(); load();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const startEdit = (o) => {
    setForm({ title:o.title||'', category:o.category||'living-room', description:o.description||'', active:o.active ?? true });
    setEditingId(o._id); setFiles([]); setShowForm(true);
  };

  const del = async (id) => { if (!window.confirm('Delete?')) return undefined; await api.delete(`/gallery/${id}`); toast.success('Deleted'); load(); };
  const toggleActive = async (o) => { await api.patch(`/gallery/${o._id}`, { active:!o.active }); load(); };

  return (
    <div>
      <AdminHeading eyebrow="Gallery" title="Gallery Management" action={<button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>{ if(showForm) resetForm(); setShowForm(!showForm); }}>{showForm?'✕ Cancel':'+ Add Gallery Item'}</button>} />

      {showForm && (
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', marginBottom:'2rem' }}>
          <form onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group">
                <label>Category *</label>
                <select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
                  {CATS.map(c=><option key={c} value={c}>{c.replace(/-/g,' ')}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Item Name *</label><input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} required placeholder="e.g. Modern Living Room Makeover" /></div>
            </div>
            <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} rows={3} placeholder="Describe this project..." /></div>
            <div className="form-group">
              <label>Gallery Image{editingId?'':' *'} (multiple)</label>
              <input type="file" accept="image/*" multiple onChange={e=>setFiles([...e.target.files])} style={{ color:'#8a8278', padding:'0.5rem 0' }} />
              {files.length>0 && <p style={{ color:'#F5A800', fontSize:'0.72rem', marginTop:'0.3rem' }}>{files.length} image(s) selected</p>}
            </div>
            <div style={{ display:'flex', gap:'0.75rem' }}>
              <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':(editingId?'Update Item':'Save Item')}</button>
              <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={()=>{ setShowForm(false); resetForm(); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : (
        <div className="grid-3" style={{ gap:'1rem' }}>
          {items.length===0 && <p style={{ gridColumn:'1/-1', textAlign:'center', padding:'4rem', color:'#8a8278' }}>No gallery items yet.</p>}
          {items.map(o => (
            <div key={o._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', overflow:'hidden' }}>
              {o.images?.[0] && <img src={imgSrc(o.images[0])} alt={o.title} style={{ width:'100%', height:'180px', objectFit:'cover' }} />}
              <div style={{ padding:'1.1rem' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.5rem' }}>
                  <h4 style={{ fontFamily:'var(--font-display)', fontSize:'1rem', fontWeight:400, color:'#f8f5ef', flex:1, marginRight:'0.5rem' }}>{o.title}</h4>
                  {/* {!o.active && <span style={{ color:'#8a8278', fontSize:'0.62rem' }}>INACTIVE</span>} */}
                </div>
                <span className="badge badge-blue" style={{ marginBottom:'0.5rem', display:'inline-block', fontSize:'0.62rem' }}>{o.category?.replace(/-/g,' ')}</span>
                <div style={{ display:'flex', gap:'0.4rem' }}>
                  <button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>startEdit(o)}>Edit</button>
                  <button onClick={()=>del(o._id)} style={{ flex:1, padding:'0.35rem', fontSize:'0.68rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── OFFERS ────────────────────────────────────────────────────────────────

export function AdminOffers() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    
    category: 'living-room',
    offerLabel: 'OFFER',
    actualPrice: '',
    actualPriceSuffix: 'Lac',
    offerPrice: '',
    offerPriceSuffix: 'Lac*',
    validTill: '',
    description: '',
    active: true
  });

  const resetForm = () => {
    setForm({
      
      category: 'living-room',
      offerLabel: 'OFFER',
      actualPrice: '',
      actualPriceSuffix: 'Lac',
      offerPrice: '',
      offerPriceSuffix: 'Lac*',
      validTill: '',
      description: '',
      active: true
    });
    setFiles([]);
    setEditingId(null);
  };
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/offers/all').then(r=>setOffers(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!editingId && !files.length) { toast.error('Add at least one image'); return undefined; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v])=>fd.append(k,v));
      files.forEach(f=>fd.append('images',f));
      if (editingId) {
        await api.patch(`/offers/${editingId}`, fd, { headers:{'Content-Type':'multipart/form-data'} });
        toast.success('Offer updated!');
      } else {
        await api.post('/offers', fd, { headers:{'Content-Type':'multipart/form-data'} });
        toast.success('Offer added!');
      }
      setShowForm(false); resetForm(); load();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const startEdit = (o) => {
    setForm({
      title: o.title || '',
      category: o.category || 'living-room',
      offerLabel: o.offerLabel || 'OFFER',
      actualPrice: o.actualPrice || '',
      actualPriceSuffix: o.actualPriceSuffix || 'Lac',
      offerPrice: o.offerPrice || '',
      offerPriceSuffix: o.offerPriceSuffix || 'Lac*',
      validTill: o.validTill ? o.validTill.slice(0,10) : '',
      description: o.description || '',
      active: o.active ?? true
    });
    setEditingId(o._id); setFiles([]); setShowForm(true);
  };

  const del = async (id) => { if (!window.confirm('Delete?')) return undefined; await api.delete(`/offers/${id}`); toast.success('Deleted'); load(); };
  const toggleActive = async (o) => { await api.patch(`/offers/${o._id}`, { active:!o.active }); load(); };

  return (
    <div>
      <AdminHeading eyebrow="Promotions" title="Offers Management" action={<button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>{ if(showForm) resetForm(); setShowForm(!showForm); }}>{showForm?'✕ Cancel':'+ Add Offer'}</button>} />

      {showForm && (
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', marginBottom:'2rem' }}>
          <form onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group">
                <label>Category *</label>
                <select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
                  {CATS.map(c=><option key={c} value={c}>{c.replace(/-/g,' ')}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Offer Label</label>
                <input value={form.offerLabel} onChange={e=>setForm(p=>({...p,offerLabel:e.target.value}))} placeholder="e.g. OFFER" />
              </div>
{/* 
              <div className="form-group">
                <label>Package Title *</label>
                <input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} required placeholder="e.g. 1BHK" />
              </div> */}

              <div className="form-group">
                <label>Actual Price *</label>
                <input value={form.actualPrice} onChange={e=>setForm(p=>({...p,actualPrice:e.target.value}))} required placeholder="e.g. 7.5" />
              </div>

              <div className="form-group">
                <label>Actual Price Suffix</label>
                <input value={form.actualPriceSuffix} onChange={e=>setForm(p=>({...p,actualPriceSuffix:e.target.value}))} placeholder="e.g. Lac" />
              </div>

              <div className="form-group">
                <label>Offer Price *</label>
                <input value={form.offerPrice} onChange={e=>setForm(p=>({...p,offerPrice:e.target.value}))} required placeholder="e.g. 4.5" />
              </div>

              <div className="form-group">
                <label>Offer Price Suffix</label>
                <input value={form.offerPriceSuffix} onChange={e=>setForm(p=>({...p,offerPriceSuffix:e.target.value}))} placeholder="e.g. Lac*" />
              </div>

              <div className="form-group">
                <label>Valid Till</label>
                <input type="date" value={form.validTill} onChange={e=>setForm(p=>({...p,validTill:e.target.value}))} />
              </div>
            </div>

            <div className="form-group">
              <label style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer', textTransform:'none', letterSpacing:'normal', fontSize:'0.82rem', color:'#ede8de' }}>
                <input type="checkbox" checked={form.active} onChange={e=>setForm(p=>({...p,active:e.target.checked}))} /> Active (visible on site)
              </label>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} rows={3} placeholder="Describe this offer..." />
            </div>

            <div className="form-group">
              <label>Offer Image{editingId?'':' *'} (multiple)</label>
              <input type="file" accept="image/*" multiple onChange={e=>setFiles([...e.target.files])} style={{ color:'#8a8278', padding:'0.5rem 0' }} />
              {files.length>0 && <p style={{ color:'#F5A800', fontSize:'0.72rem', marginTop:'0.3rem' }}>{files.length} image(s) selected</p>}
            </div>

            <div style={{ display:'flex', gap:'0.75rem' }}>
              <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':(editingId?'Update Offer':'Save Offer')}</button>
              <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={()=>{ setShowForm(false); resetForm(); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : (
        <div className="grid-3" style={{ gap:'1rem' }}>
          {offers.length===0 && <p style={{ gridColumn:'1/-1', textAlign:'center', padding:'4rem', color:'#8a8278' }}>No offers yet.</p>}
          {offers.map(o => (
            <div key={o._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', overflow:'hidden' }}>
              {o.images?.[0] && <img src={imgSrc(o.images[0])} alt={o.title} style={{ width:'100%', height:'180px', objectFit:'cover' }} />}
              <div style={{ padding:'1.1rem' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.4rem', flexWrap:'wrap' }}>
                  <span style={{ color:'#8a8278', fontSize:'0.72rem', fontWeight:600, letterSpacing:'0.05em' }}>{o.offerLabel || 'OFFER'}</span>
                  <span style={{ position:'relative', color:'#8a8278', fontSize:'0.85rem', fontWeight:500 }}>
                    ₹{o.actualPrice} {o.actualPriceSuffix}
                    <span style={{ position:'absolute', left:0, right:0, top:'50%', height:'2px', background:'#e11d48', transform:'rotate(-8deg)' }} />
                  </span>
                </div>

                <div style={{ marginBottom:'0.6rem' }}>
                  <span style={{ color:'#F5A800', fontSize:'1.15rem', fontWeight:700 }}>
                    ₹{o.offerPrice} {o.offerPriceSuffix}
                  </span>
                </div>

                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.5rem' }}>
                  <h4 style={{ fontFamily:'var(--font-display)', fontSize:'1rem', fontWeight:400, color:'#f8f5ef', flex:1, marginRight:'0.5rem', textTransform:'uppercase', letterSpacing:'0.02em' }}>{o.title}</h4>
                  {!o.active && <span style={{ color:'#8a8278', fontSize:'0.62rem' }}>INACTIVE</span>}
                </div>

                {o.validTill && <p style={{ color:'#8a8278', fontSize:'0.72rem', marginBottom:'0.75rem' }}>Valid till {new Date(o.validTill).toLocaleDateString()}</p>}

                <div style={{ display:'flex', gap:'0.4rem' }}>
                  <button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>startEdit(o)}>Edit</button>
                  <button onClick={()=>toggleActive(o)} className="btn btn-dark" style={{ fontSize:'0.72rem' }}>{o.active?'Deactivate':'Activate'}</button>
                  <button onClick={()=>del(o._id)} style={{ flex:1, padding:'0.35rem', fontSize:'0.68rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}



// ─── CAREERS ──────────────────────────────────────────────────────────────────
export function AdminCareers() {
  const [jobs, setJobs] = useState([]);
  const [apps, setApps] = useState([]);
  const [tab, setTab] = useState('jobs');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title:'', department:'', location:'Tumakuru, Karnataka', type:'full-time', experience:'', salary:'', description:'', requirements:'' });
  const [saving, setSaving] = useState(false);

  const load = () => {
    Promise.all([api.get('/careers/all'), api.get('/careers/applications')])
      .then(([j,a])=>{ setJobs(j.data); setApps(a.data); }).catch(()=>{}).finally(()=>setLoading(false));
  };
  useEffect(()=>{load();},[]);

  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/careers', { ...form, requirements:form.requirements.split('\n').filter(Boolean) });
      toast.success('Job posted!'); setShowForm(false); setForm({ title:'', department:'', location:'Tumakuru, Karnataka', type:'full-time', experience:'', salary:'', description:'', requirements:'' }); load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const del = async (id) => { if (!window.confirm('Delete?')) return undefined; await api.delete(`/careers/${id}`); load(); toast.success('Deleted'); };
  const updateApp = async (id, status) => { await api.patch(`/careers/applications/${id}`, { status }); load(); toast.success('Updated'); };

  return (
    <div>
      <AdminHeading eyebrow="HR" title="Careers Management" action={tab==='jobs'&&<button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>setShowForm(!showForm)}>{showForm?'✕ Cancel':'+ Post Job'}</button>} />
      <div style={{ display:'flex', gap:'0.5rem', marginBottom:'2rem' }}>
        {['jobs','applications'].map(t=>(
          <button key={t} className={`btn ${tab===t?'btn-gold':'btn-dark'}`} style={{ padding:'0.5rem 1.25rem', fontSize:'0.72rem', textTransform:'capitalize' }} onClick={()=>setTab(t)}>
            {t} ({t==='jobs'?jobs.length:apps.length})
          </button>
        ))}
      </div>
      {showForm && tab==='jobs' && (
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', marginBottom:'2rem' }}>
          <form onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group"><label>Job Title *</label><input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} required /></div>
              <div className="form-group"><label>Department</label><input value={form.department} onChange={e=>setForm(p=>({...p,department:e.target.value}))} placeholder="e.g. Design" /></div>
              <div className="form-group"><label>Type</label><select value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))}><option value="full-time">Full Time</option><option value="part-time">Part Time</option><option value="contract">Contract</option><option value="internship">Internship</option></select></div>
              <div className="form-group"><label>Experience</label><input value={form.experience} onChange={e=>setForm(p=>({...p,experience:e.target.value}))} placeholder="e.g. 2-4 years" /></div>
              <div className="form-group"><label>Salary Range</label><input value={form.salary} onChange={e=>setForm(p=>({...p,salary:e.target.value}))} placeholder="e.g. ₹4-6 LPA" /></div>
              <div className="form-group"><label>Location</label><input value={form.location} onChange={e=>setForm(p=>({...p,location:e.target.value}))} /></div>
            </div>
            <div className="form-group"><label>Job Description *</label><textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} rows={4} required /></div>
            <div className="form-group"><label>Requirements (one per line)</label><textarea value={form.requirements} onChange={e=>setForm(p=>({...p,requirements:e.target.value}))} rows={4} placeholder="AutoCAD skills&#10;3+ years experience" /></div>
            <div style={{ display:'flex', gap:'0.75rem' }}>
              <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Posting...':'Post Job'}</button>
              <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={()=>setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : tab==='jobs' ? (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {jobs.length===0 && <p style={{ color:'#8a8278', textAlign:'center', padding:'4rem' }}>No jobs posted.</p>}
          {jobs.map(j=>(
            <div key={j._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', padding:'1.5rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'1rem' }}>
                <div>
                  <h4 style={{ fontFamily:'var(--font-display)', fontSize:'1.1rem', fontWeight:400, color:'#f8f5ef', marginBottom:'0.5rem' }}>{j.title}</h4>
                  <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap' }}>
                    {[j.type,j.experience,j.salary].filter(Boolean).map(t=><span key={t} className="badge badge-gold" style={{ fontSize:'0.62rem' }}>{t}</span>)}
                    <span className={`badge ${j.isActive?'badge-green':'badge-red'}`} style={{ fontSize:'0.62rem' }}>{j.isActive?'Active':'Closed'}</span>
                  </div>
                </div>
                <div style={{ display:'flex', gap:'0.5rem' }}>
                  <button onClick={()=>api.patch(`/careers/${j._id}`,{isActive:!j.isActive}).then(load)} className="btn btn-dark" style={{ padding:'0.35rem 0.7rem', fontSize:'0.68rem' }}>{j.isActive?'Close':'Reopen'}</button>
                  <button onClick={()=>del(j._id)} style={{ padding:'0.35rem 0.7rem', fontSize:'0.68rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {apps.length===0 && <p style={{ color:'#8a8278', textAlign:'center', padding:'4rem' }}>No applications yet.</p>}
          {apps.map(a=>(
            <div key={a._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', padding:'1.5rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'1rem' }}>
                <div>
                  <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', marginBottom:'0.3rem' }}>
                    <h4 style={{ fontSize:'0.9rem', color:'#f8f5ef', fontWeight:500 }}>{a.name}</h4>
                    <span className={`badge ${a.status==='new'?'badge-gold':a.status==='shortlisted'?'badge-green':'badge-muted'}`} style={{ fontSize:'0.62rem' }}>{a.status}</span>
                  </div>
                  <p style={{ color:'#8a8278', fontSize:'0.76rem' }}>{a.email} · {a.phone}</p>
                  {a.jobTitle && <p style={{ color:'#F5A800', fontSize:'0.72rem', marginTop:'0.2rem' }}>Applied: {a.jobTitle}</p>}
                  {a.experience && <p style={{ color:'#8a8278', fontSize:'0.72rem' }}>Exp: {a.experience}</p>}
                </div>
                <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap' }}>
                  {a.resume && <a href={imgSrc(a.resume)} target="_blank" rel="noopener noreferrer" className="btn btn-dark" style={{ padding:'0.32rem 0.6rem', fontSize:'0.65rem' }}>Resume</a>}
                  {['reviewed','shortlisted','rejected'].map(s=>(
                    <button key={s} className="btn btn-dark" style={{ padding:'0.32rem 0.6rem', fontSize:'0.65rem' }} onClick={()=>updateApp(a._id,s)}>{s}</button>
                  ))}
                </div>
              </div>
              {a.coverLetter && <p style={{ color:'#8a8278', fontSize:'0.78rem', lineHeight:'1.7', marginTop:'0.85rem', borderTop:'1px solid rgba(255,255,255,0.04)', paddingTop:'0.85rem' }}>{a.coverLetter}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── CONTACTS ─────────────────────────────────────────────────────────────────
export function AdminContacts() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = () => api.get('/contacts').then(r=>setContacts(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);
  const update = async (id, status) => { await api.patch(`/contacts/${id}`,{status}); load(); toast.success('Updated'); };
  const del = async (id) => { if (!window.confirm('Delete?')) return undefined; await api.delete(`/contacts/${id}`); load(); toast.success('Deleted'); };

  return (
    <div>
      <AdminHeading eyebrow="Enquiries" title="Contact Messages" />
      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {contacts.length===0 && <p style={{ color:'#8a8278', textAlign:'center', padding:'4rem' }}>No messages yet.</p>}
          {contacts.map(c=>(
            <div key={c._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', padding:'1.5rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.75rem', flexWrap:'wrap', gap:'0.5rem' }}>
                <div>
                  <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
                    <h4 style={{ fontSize:'0.9rem', color:'#f8f5ef', fontWeight:500 }}>{c.name}</h4>
                    <span className={`badge ${c.status==='new'?'badge-gold':c.status==='replied'?'badge-green':'badge-blue'}`} style={{ fontSize:'0.62rem' }}>{c.status}</span>
                  </div>
                  <p style={{ color:'#8a8278', fontSize:'0.75rem' }}>{c.email}{c.phone&&` · ${c.phone}`}</p>
                  {c.subject && <p style={{ color:'#F5A800', fontSize:'0.72rem', marginTop:'0.2rem' }}>{c.subject}</p>}
                </div>
                <span style={{ color:'#8a8278', fontSize:'0.68rem' }}>{new Date(c.createdAt).toLocaleDateString('en-IN')}</span>
              </div>
              <p style={{ color:'#ede8de', fontSize:'0.84rem', lineHeight:'1.7', marginBottom:'1rem' }}>{c.message}</p>
              <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                {['read','replied'].map(s=><button key={s} className="btn btn-dark" style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem' }} onClick={()=>update(c._id,s)}>{s}</button>)}
                <a href={`mailto:${c.email}?subject=Re: ${c.subject||'Your Enquiry'}`} className="btn btn-gold" style={{ padding:'0.32rem 0.9rem', fontSize:'0.68rem' }}>Reply</a>
                <button onClick={()=>del(c._id)} style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── NUMBER TO WORDS (Indian numbering) ────────────────────────────────────────
const numberToWordsIndian = (num) => {
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const twoDigits = (n) => n < 20 ? ones[n] : (tens[Math.floor(n/10)] + (n%10 ? ' '+ones[n%10] : ''));
  const threeDigits = (n) => (n>=100 ? ones[Math.floor(n/100)]+' Hundred'+(n%100?' '+twoDigits(n%100):'') : twoDigits(n));
  const inWords = (n) => {
    if (n === 0) return 'Zero';
    let parts = [];
    const crore = Math.floor(n/10000000); n %= 10000000;
    const lakh = Math.floor(n/100000); n %= 100000;
    const thousand = Math.floor(n/1000); n %= 1000;
    const rest = n;
    if (crore) parts.push(twoDigits(crore)+' Crore');
    if (lakh) parts.push(twoDigits(lakh)+' Lakh');
    if (thousand) parts.push(twoDigits(thousand)+' Thousand');
    if (rest) parts.push(threeDigits(rest));
    return parts.join(' ');
  };
  const amount = Math.round((parseFloat(num)||0) * 100) / 100;
  const rupees = Math.floor(amount);
  const paise = Math.round((amount - rupees) * 100);
  let words = inWords(rupees) + ' Rupees';
  if (paise) words += ' and ' + inWords(paise) + ' Paise';
  return words + ' Only';
};

const fmtAmt = (n) => (parseFloat(n)||0).toLocaleString('en-IN', { minimumFractionDigits:2, maximumFractionDigits:2 });

// crops rows/cols that are near-white or transparent off the edges of a canvas
const trimCanvasWhitespace = (canvas) => {
  try {
    const ctx = canvas.getContext('2d');
    const w = canvas.width, h = canvas.height;
    const { data } = ctx.getImageData(0, 0, w, h);
    const isBlank = (i) => { if (data[i+3] < 10) return true; return data[i] > 245 && data[i+1] > 245 && data[i+2] > 245; };
    const rowBlank = (y) => { for (let x=0;x<w;x++) { if (!isBlank((y*w+x)*4)) return false; } return true; };
    const colBlank = (x) => { for (let y=0;y<h;y++) { if (!isBlank((y*w+x)*4)) return false; } return true; };
    let top=0, bottom=h-1, left=0, right=w-1;
    while (top<h-1 && rowBlank(top)) top++;
    while (bottom>top && rowBlank(bottom)) bottom--;
    while (left<w-1 && colBlank(left)) left++;
    while (right>left && colBlank(right)) right--;
    if (right<=left || bottom<=top) return canvas;
    const tw = right-left+1, th = bottom-top+1;
    const out = document.createElement('canvas');
    out.width = tw; out.height = th;
    out.getContext('2d').drawImage(canvas, left, top, tw, th, 0, 0, tw, th);
    return out;
  } catch { return canvas; }
};

// loads an image (any src), trims whitespace, returns a portable dataURL asset; optionally samples corner bg color
export const loadImageAsset = (src, opts={}) => new Promise((resolve) => {
  if (!src) return resolve(null);
  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const w = img.naturalWidth || img.width, h = img.naturalHeight || img.height;
      let canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      canvas.getContext('2d').drawImage(img, 0, 0);
      canvas = trimCanvasWhitespace(canvas);
      const tw = canvas.width, th = canvas.height;
      const result = { dataUrl: canvas.toDataURL('image/png'), width: tw, height: th };
      if (opts.sampleBg) {
        const ctx = canvas.getContext('2d');
        let bg = { r:15, g:64, b:74 };
        try {
          const corners = [ctx.getImageData(0,0,1,1).data, ctx.getImageData(tw-1,0,1,1).data, ctx.getImageData(0,th-1,1,1).data, ctx.getImageData(tw-1,th-1,1,1).data];
          bg = { r: Math.round(corners.reduce((s,c)=>s+c[0],0)/4), g: Math.round(corners.reduce((s,c)=>s+c[1],0)/4), b: Math.round(corners.reduce((s,c)=>s+c[2],0)/4) };
        } catch {}
        result.bg = bg;
      }
      resolve(result);
    } catch { resolve(null); }
  };
  img.onerror = () => resolve(null);
  img.src = src;
});

const QUOTE_CATS = ['bathroom','false-ceiling','commercial','complete-modular','office','first living','first floor kitchen','second floor bedroom 1','second floor bedroom 2','third floor bedroom 1','third floor bedroom 2'];
// ─── SOCIAL ───────────────────────────────────────────────────────────────────
export function AdminSocial() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ platform:'instagram', postUrl:'', embedCode:'', thumbnail:'', caption:'', likes:0 });
  const [saving, setSaving] = useState(false);
  const load = () => api.get('/social').then(r=>setPosts(Array.isArray(r.data)?r.data:(r.data?.posts||r.data?.data||[]))).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);
  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post('/social', form); toast.success('Post added!'); setShowForm(false); setForm({ platform:'instagram', postUrl:'', embedCode:'', thumbnail:'', caption:'', likes:0 }); load(); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/social/${id}`); load(); toast.success('Removed'); };

  return (
    <div>
      <AdminHeading eyebrow="Social Feed" title="Social Media Posts" action={<button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>setShowForm(!showForm)}>{showForm?'✕ Cancel':'+ Add Post'}</button>} />
      <div style={{ background:'rgba(245,168,0,0.05)', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'1rem 1.5rem', marginBottom:'1.5rem' }}>
        <p style={{ color:'#8a8278', fontSize:'0.8rem', lineHeight:'1.7' }}><strong style={{ color:'#F5A800' }}>How to add posts:</strong> On Instagram/Facebook, go to your post → Share → Embed → copy the embed code. Or paste the post URL + a thumbnail image URL. These posts display in the homepage social section.</p>
      </div>
      {showForm && (
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', marginBottom:'2rem' }}>
          <form onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group"><label>Platform</label><select value={form.platform} onChange={e=>setForm(p=>({...p,platform:e.target.value}))}><option value="instagram">Instagram</option><option value="facebook">Facebook</option><option value="both">Both</option></select></div>
              <div className="form-group"><label>Post URL *</label><input value={form.postUrl} onChange={e=>setForm(p=>({...p,postUrl:e.target.value}))} required placeholder="https://instagram.com/p/..." /></div>
              <div className="form-group"><label>Thumbnail URL</label><input value={form.thumbnail} onChange={e=>setForm(p=>({...p,thumbnail:e.target.value}))} placeholder="https://..." /></div>
              <div className="form-group"><label>Likes Count</label><input type="number" value={form.likes} onChange={e=>setForm(p=>({...p,likes:e.target.value}))} /></div>
            </div>
            <div className="form-group"><label>Caption</label><input value={form.caption} onChange={e=>setForm(p=>({...p,caption:e.target.value}))} placeholder="Post caption..." /></div>
            <div className="form-group"><label>Embed Code (optional)</label><textarea value={form.embedCode} onChange={e=>setForm(p=>({...p,embedCode:e.target.value}))} rows={4} placeholder="Paste Instagram/Facebook embed code here..." /></div>
            <div style={{ display:'flex', gap:'0.75rem' }}>
              <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':'Add Post'}</button>
              <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={()=>setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : (
        <div className="grid-4" style={{ gap:'1rem' }}>
          {posts.length===0 && <p style={{ gridColumn:'1/-1', color:'#8a8278', textAlign:'center', padding:'4rem' }}>No posts added yet.</p>}
          {posts.map(p=>(
            <div key={p._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', overflow:'hidden' }}>
              <div style={{ height:'140px', background:'#1c1c1c', position:'relative' }}>
                {p.thumbnail ? <img src={p.thumbnail} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> :
                  <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'2rem' }}>{p.platform==='instagram'?'📷':'👍'}</div>}
              </div>
              <div style={{ padding:'0.85rem' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.4rem' }}>
                  <span className="badge badge-gold" style={{ fontSize:'0.58rem' }}>{p.platform}</span>
                  {p.likes && <span style={{ color:'#8a8278', fontSize:'0.68rem' }}>❤ {p.likes}</span>}
                </div>
                {p.caption && <p style={{ color:'#8a8278', fontSize:'0.72rem', marginBottom:'0.6rem' }}>{p.caption?.slice(0,50)}...</p>}
                <button onClick={()=>del(p._id)} style={{ width:'100%', padding:'0.32rem', fontSize:'0.68rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── TESTIMONIALS ──────────────────────────────────────────────────────────────
export function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name:'', location:'', rating:5, review:'', projectType:'', isActive:true });
  const [saving, setSaving] = useState(false);
  const load = () => api.get('/testimonials/all').then(r=>setItems(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);
  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post('/testimonials', form); toast.success('Testimonial added!'); setShowForm(false); setForm({ name:'', location:'', rating:5, review:'', projectType:'', isActive:true }); load(); }
    catch { toast.error('Failed'); } finally { setSaving(false); }
  };
  const del = async (id) => { await api.delete(`/testimonials/${id}`); load(); toast.success('Deleted'); };
  const toggle = async (t) => { await api.patch(`/testimonials/${t._id}`, { isActive:!t.isActive }); load(); };

  return (
    <div>
      <AdminHeading eyebrow="Client Reviews" title="Testimonials" action={<button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>setShowForm(!showForm)}>{showForm?'✕ Cancel':'+ Add Review'}</button>} />
      {showForm && (
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', marginBottom:'2rem' }}>
          <form onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group"><label>Client Name *</label><input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} required /></div>
              <div className="form-group"><label>Location</label><input value={form.location} onChange={e=>setForm(p=>({...p,location:e.target.value}))} placeholder="e.g. Tumakuru" /></div>
              <div className="form-group"><label>Rating (1-5)</label><select value={form.rating} onChange={e=>setForm(p=>({...p,rating:Number(e.target.value)}))}>{[5,4,3,2,1].map(r=><option key={r} value={r}>{''.repeat(r)} ({r})</option>)}</select></div>
              <div className="form-group"><label>Project Type</label><input value={form.projectType} onChange={e=>setForm(p=>({...p,projectType:e.target.value}))} placeholder="e.g. Modular Kitchen" /></div>
            </div>
            <div className="form-group"><label>Review *</label><textarea value={form.review} onChange={e=>setForm(p=>({...p,review:e.target.value}))} rows={4} required placeholder="Client's review..." /></div>
            <div style={{ display:'flex', gap:'0.75rem' }}>
              <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':'Add Testimonial'}</button>
              <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={()=>setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {items.length===0 && <p style={{ color:'#8a8278', textAlign:'center', padding:'4rem' }}>No testimonials yet.</p>}
          {items.map(t=>(
            <div key={t._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', padding:'1.5rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:'1rem' }}>
                <div>
                  <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', marginBottom:'0.3rem' }}>
                    <h4 style={{ fontSize:'0.9rem', color:'#f8f5ef', fontWeight:500 }}>{t.name}</h4>
                    <span style={{ color:'#F5A800', fontSize:'0.78rem' }}>{''.repeat(t.rating||5)}</span>
                    <span className={`badge ${t.isActive?'badge-green':'badge-muted'}`} style={{ fontSize:'0.58rem' }}>{t.isActive?'Active':'Hidden'}</span>
                  </div>
                  <p style={{ color:'#8a8278', fontSize:'0.74rem' }}>{t.location}{t.projectType&&` · ${t.projectType}`}</p>
                </div>
                <div style={{ display:'flex', gap:'0.5rem' }}>
                  <button className="btn btn-dark" style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem' }} onClick={()=>toggle(t)}>{t.isActive?'Hide':'Show'}</button>
                  <button onClick={()=>del(t._id)} style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Delete</button>
                </div>
              </div>
              <p style={{ color:'#ede8de', fontSize:'0.84rem', lineHeight:'1.7', marginTop:'0.75rem', fontStyle:'italic' }}>"{t.review}"</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SEO ──────────────────────────────────────────────────────────────────────
export function AdminSEO() {
  const PAGES = ['home','about','services','portfolio','careers','contact','get-free-quote'];
  const [page, setPage] = useState('home');
  const [form, setForm] = useState({ title:'', description:'', keywords:'', ogImage:'' });
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get(`/seo/${page}`).then(r=>{ if(r.data?.title) setForm({ title:r.data.title||'', description:r.data.description||'', keywords:r.data.keywords||'', ogImage:r.data.ogImage||'' }); else setForm({ title:'', description:'', keywords:'', ogImage:'' }); }).catch(()=>{}); }, [page]);
  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.put(`/seo/${page}`, form); toast.success('SEO settings saved!'); } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  return (
    <div>
      <AdminHeading eyebrow="Search Engine" title="SEO Settings" />
      <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap', marginBottom:'2rem' }}>
        {PAGES.map(p=>(
          <button key={p} className={`btn ${page===p?'btn-gold':'btn-dark'}`} style={{ padding:'0.45rem 1rem', fontSize:'0.68rem', textTransform:'capitalize' }} onClick={()=>setPage(p)}>{p.replace(/-/g,' ')}</button>
        ))}
      </div>
      <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2.5rem' }}>
        <h3 style={{ fontFamily:'var(--font-display)', fontSize:'1.25rem', fontWeight:400, color:'#f8f5ef', marginBottom:'2rem', textTransform:'capitalize' }}>{page.replace(/-/g,' ')} Page — SEO Settings</h3>
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label>Page Title (max 60 chars) <span style={{ color:'#F5A800', fontSize:'0.7rem' }}>{form.title.length}/60</span></label>
            <input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} maxLength={60} placeholder="Wedoo Interiors | Best Interior Designers in Tumakuru" />
          </div>
          <div className="form-group">
            <label>Meta Description (max 160 chars) <span style={{ color:'#F5A800', fontSize:'0.7rem' }}>{form.description.length}/160</span></label>
            <textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} maxLength={160} rows={3} placeholder="Premium interior design in Tumakuru, Karnataka..." />
          </div>
          <div className="form-group"><label>Keywords (comma-separated)</label><input value={form.keywords} onChange={e=>setForm(p=>({...p,keywords:e.target.value}))} placeholder="wedoo interiors, interior design tumakuru, modular kitchen tumakuru..." /></div>
          <div className="form-group"><label>OG Image URL</label><input value={form.ogImage} onChange={e=>setForm(p=>({...p,ogImage:e.target.value}))} placeholder="https://wedoointeriors.com/og-image.jpg" /></div>
          <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':'Save SEO Settings'}</button>
        </form>
      </div>
    </div>
  );
}

// ─── USERS ────────────────────────────────────────────────────────────────────
export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name:'', email:'', password:'', role:'editor' });
  const [saving, setSaving] = useState(false);
  const { user:me } = useAuth();
  const load = () => api.get('/auth/users').then(r=>setUsers(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);
  const handleSave = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post('/auth/register', form); toast.success('User created!'); setShowForm(false); setForm({ name:'', email:'', password:'', role:'editor' }); load(); }
    catch (err) { toast.error(err.response?.data?.message||'Failed'); } finally { setSaving(false); }
  };
  const del = async (id) => { if (!window.confirm('Delete user?')) return undefined; try { await api.delete(`/auth/users/${id}`); load(); toast.success('Deleted'); } catch(e) { toast.error(e.response?.data?.message||'Failed'); } };

  return (
    <div>
      <AdminHeading eyebrow="Access Control" title="User Management" action={<button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>setShowForm(!showForm)}>{showForm?'✕ Cancel':'+ Add User'}</button>} />
      {showForm && (
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', marginBottom:'2rem' }}>
          <form onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group"><label>Full Name *</label><input value={form.name} onChange={e=>setForm(p=>({...p,name:e.target.value}))} required /></div>
              <div className="form-group"><label>Email *</label><input type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} required /></div>
              <div className="form-group"><label>Password * (min 8 chars)</label><input type="password" value={form.password} onChange={e=>setForm(p=>({...p,password:e.target.value}))} required minLength={8} /></div>
              <div className="form-group"><label>Role</label><select value={form.role} onChange={e=>setForm(p=>({...p,role:e.target.value}))}><option value="editor">Editor</option><option value="admin">Admin</option></select></div>
            </div>
            <div style={{ display:'flex', gap:'0.75rem' }}>
              <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Creating...':'Create User'}</button>
              <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={()=>setShowForm(false)}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {users.map(u=>(
            <div key={u._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', padding:'1.25rem', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'1rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'1rem' }}>
                <div style={{ width:'44px', height:'44px', borderRadius:'50%', background:'rgba(245,168,0,0.12)', border:'1px solid rgba(245,168,0,0.25)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'var(--font-display)', color:'#F5A800', fontSize:'1.2rem' }}>{u.name?.[0]?.toUpperCase()}</div>
                <div>
                  <p style={{ fontSize:'0.88rem', color:'#f8f5ef', fontWeight:500 }}>{u.name} {u._id===me?._id&&<span style={{ color:'#F5A800', fontSize:'0.68rem' }}>(you)</span>}</p>
                  <p style={{ fontSize:'0.74rem', color:'#8a8278' }}>{u.email}</p>
                </div>
              </div>
              <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
                <span className={`badge ${u.role==='admin'?'badge-gold':'badge-blue'}`} style={{ fontSize:'0.62rem' }}>{u.role}</span>
                <span className={`badge ${u.isActive?'badge-green':'badge-red'}`} style={{ fontSize:'0.62rem' }}>{u.isActive?'Active':'Inactive'}</span>
                {u._id!==me?._id && <button onClick={()=>del(u._id)} style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Delete</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MAINTENANCE ──────────────────────────────────────────────────────────────
export function AdminMaintenance() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const STATUSES = ['pending','in-progress','resolved'];
  const [form, setForm] = useState({ clientName:'', phone:'', title:'', description:'', status:'pending' });
  const load = () => api.get('/maintenance').then(r=>setItems(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);
  const resetForm = () => setForm({ clientName:'', phone:'', title:'', description:'', status:'pending' });
  const update = async (id, status) => { await api.patch(`/maintenance/${id}`,{status}); load(); toast.success('Updated'); };
  const del = async (id) => { if (!window.confirm('Delete?')) return undefined; await api.delete(`/maintenance/${id}`); load(); toast.success('Deleted'); };
  const handleSave = async (e) => {
    e.preventDefault();
    if (!isValidOptionalPhone(form.phone)) { toast.error(PHONE_ERROR); return; }
    setSaving(true);
    try {
      await api.post('/maintenance', form);
      toast.success('Maintenance request added!'); setShowForm(false); resetForm(); load();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  return (
    <div>
      <AdminHeading eyebrow="Operations" title="Maintenance" action={<button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>{ if(showForm) resetForm(); setShowForm(!showForm); }}>{showForm?'✕ Cancel':'+ Add Maintenance'}</button>} />
      {showForm && (
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', marginBottom:'2rem' }}>
          <form onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group"><label>Client Name</label><input value={form.clientName} onChange={e=>setForm(p=>({...p,clientName:e.target.value}))} placeholder="e.g. Rahul Sharma" /></div>
              <div className="form-group"><label>Phone</label><input {...phoneInputProps} value={form.phone} onChange={e=>setForm(p=>({...p,phone:sanitizePhone(e.target.value)}))} placeholder="e.g. 9876543210" /></div>
              <div className="form-group"><label>Issue / Title</label><input value={form.title} onChange={e=>setForm(p=>({...p,title:e.target.value}))} placeholder="e.g. Wardrobe hinge repair" /></div>
              <div className="form-group">
                <label>Status</label>
                <select value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))}>
                  {STATUSES.map(s=><option key={s} value={s}>{s.replace(/-/g,' ')}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group"><label>Description</label><textarea value={form.description} onChange={e=>setForm(p=>({...p,description:e.target.value}))} rows={3} placeholder="Describe the maintenance issue..." /></div>
            <div style={{ display:'flex', gap:'0.75rem' }}>
              <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':'Save'}</button>
              <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={()=>{ setShowForm(false); resetForm(); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {items.length===0 && <p style={{ color:'#8a8278', textAlign:'center', padding:'4rem' }}>No maintenance requests yet.</p>}
          {items.map(m=>(
            <div key={m._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', padding:'1.5rem' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.75rem', flexWrap:'wrap', gap:'0.5rem' }}>
                <div>
                  <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
                    <h4 style={{ fontSize:'0.9rem', color:'#f8f5ef', fontWeight:500 }}>{m.title}</h4>
                    <span className={`badge ${m.status==='resolved'?'badge-green':m.status==='in-progress'?'badge-blue':'badge-gold'}`} style={{ fontSize:'0.62rem' }}>{m.status}</span>
                  </div>
                  <p style={{ color:'#8a8278', fontSize:'0.75rem' }}>{m.clientName}{m.phone&&` · ${m.phone}`}</p>
                </div>
                <span style={{ color:'#8a8278', fontSize:'0.68rem' }}>{new Date(m.createdAt).toLocaleDateString('en-IN')}</span>
              </div>
              {m.description && <p style={{ color:'#ede8de', fontSize:'0.84rem', lineHeight:'1.7', marginBottom:'1rem' }}>{m.description}</p>}
              <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
                {STATUSES.map(s=><button key={s} className="btn btn-dark" style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem' }} onClick={()=>update(m._id,s)}>{s.replace(/-/g,' ')}</button>)}
                <button onClick={()=>del(m._id)} style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── MAINTENANCE ITEMS ────────────────────────────────────────────────────────
const HSN_SEQ_START = 9401;
const nextSequentialHsn = (existingItems, usedInThisRun) => {
  const used = new Set(existingItems.filter(it=>it.hsn).map(it=>it.hsn));
  usedInThisRun.forEach(h=>used.add(h));
  let n = HSN_SEQ_START;
  while (used.has(String(n))) n++;
  return String(n);
};
export function AdminMaintenanceItem() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [bulkFilling, setBulkFilling] = useState(false);
  const [form, setForm] = useState({ category:'bathroom', item:'', hsn:'', unit:'NOS', rate:'' });
  const load = () => api.get('/maintenance-items').then(r=>setItems(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);
  const resetForm = () => setForm({ category:'bathroom', item:'', hsn:'', unit:'NOS', rate:'' });
  const del = async (id) => { if (!window.confirm('Delete?')) return undefined; await api.delete(`/maintenance-items/${id}`); load(); toast.success('Deleted'); };
  const startEdit = (it) => { setShowForm(false); setEditingId(it._id); setForm({ category: it.category || 'bathroom', item: it.item || '', hsn: it.hsn || '', unit: it.unit || 'NOS', rate: it.rate!=null ? String(it.rate) : '' }); };
  const cancelEdit = () => { setEditingId(null); resetForm(); };
  const toggleActive = async (it) => {
    try { await api.patch(`/maintenance-items/${it._id}`, { active: !(it.active !== false) }); load(); toast.success(it.active === false ? 'Marked active' : 'Marked inactive'); }
    catch { toast.error('Failed to update status'); }
  };
  const openAddForm = () => {
    setEditingId(null);
    setForm(p => ({ ...p, hsn: nextSequentialHsn(items, []) }));
    setShowForm(true);
  };
  const fillMissingHsn = async () => {
    const missing = items.filter(it => !it.hsn);
    if (missing.length === 0) { toast.success('Every item already has an HSN code'); return; }
    if (!window.confirm(`Assign sequential HSN codes (starting ${HSN_SEQ_START}) to ${missing.length} item(s) that are missing one?`)) return;
    setBulkFilling(true);
    try {
      const assigned = [];
      for (const it of missing) {
        const code = nextSequentialHsn(items, assigned);
        assigned.push(code);
        await api.patch(`/maintenance-items/${it._id}`, { hsn: code });
      }
      toast.success(`HSN codes ${assigned[0]}–${assigned[assigned.length-1]} assigned to ${assigned.length} item(s)`);
      load();
    } catch { toast.error('Failed to auto-fill HSN for some items'); } finally { setBulkFilling(false); }
  };
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/maintenance-items/${editingId}`, form);
        toast.success('Item updated!');
      } else {
        await api.post('/maintenance-items', form);
        toast.success('Item added!');
      }
      setShowForm(false); setEditingId(null); resetForm(); load();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const itemFormFields = (
    <div className="grid-2">
      <div className="form-group">
        <label>Category</label>
        <select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
          {QUOTE_CATS.map(c=><option key={c} value={c}>{c.replace(/-/g,' ')}</option>)}
        </select>
      </div>
      <div className="form-group"><label>Item</label><input value={form.item} onChange={e=>setForm(p=>({...p,item:e.target.value}))} placeholder="e.g. Wardrobe Hinge" /></div>
      <div className="form-group"><label>HSN Code</label><input value={form.hsn} onChange={e=>setForm(p=>({...p,hsn:e.target.value.replace(/[^0-9]/g,'')}))} placeholder="e.g. 48239019" /></div>
      <div className="form-group"><label>Unit</label><input value={form.unit} onChange={e=>setForm(p=>({...p,unit:e.target.value}))} placeholder="e.g. NOS, SFT" /></div>
      <div className="form-group"><label>Rate</label><input value={form.rate} onChange={e=>setForm(p=>({...p,rate:e.target.value.replace(/[^0-9.]/g,'')}))} placeholder="e.g. 1200" /></div>
    </div>
  );

  return (
    <div>
      <AdminHeading eyebrow="Maintenance" title="Item" action={
        <div style={{ display:'flex', gap:'0.6rem', flexWrap:'wrap' }}>
          <button className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={fillMissingHsn} disabled={bulkFilling}>{bulkFilling?'Filling...':'Auto-fill HSN (sequential)'}</button>
          <button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>{ if(showForm){ resetForm(); setShowForm(false); } else { openAddForm(); } }}>{showForm?'✕ Cancel':'+ Add Item'}</button>
        </div>
      } />
      {showForm && (
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', marginBottom:'2rem' }}>
          <form onSubmit={handleSave}>
            {itemFormFields}
            <div style={{ display:'flex', gap:'0.75rem' }}>
              <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':'Save'}</button>
              <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={()=>{ setShowForm(false); resetForm(); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {items.length===0 && <p style={{ color:'#8a8278', textAlign:'center', padding:'4rem' }}>No items yet.</p>}
          {items.map(it=>{
            const isActive = it.active !== false;
            if (editingId === it._id) {
              return (
                <div key={it._id} style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.3)', borderRadius:'var(--radius)', padding:'1.5rem' }}>
                  <form onSubmit={handleSave}>
                    {itemFormFields}
                    <div style={{ display:'flex', gap:'0.75rem' }}>
                      <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':'Update'}</button>
                      <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={cancelEdit}>Cancel</button>
                    </div>
                  </form>
                </div>
              );
            }
            return (
            <div key={it._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', padding:'1.25rem', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.75rem', opacity: isActive?1:0.55 }}>
              <div>
                <h4 style={{ fontSize:'0.9rem', color:'#f8f5ef', fontWeight:500 }}>{it.item}</h4>
                <div style={{ display:'flex', gap:'0.4rem', marginTop:'0.3rem', flexWrap:'wrap' }}>
                  <span className="badge badge-blue" style={{ fontSize:'0.62rem', display:'inline-block' }}>{it.category?.replace(/-/g,' ')}</span>
                  {it.hsn && <span className="badge" style={{ fontSize:'0.62rem', display:'inline-block', background:'rgba(148,163,184,0.1)', color:'#8a8278', border:'1px solid rgba(148,163,184,0.2)', borderRadius:'var(--radius)' }}>HSN {it.hsn}</span>}
                  {it.rate!=null && it.rate!=='' && <span className="badge" style={{ fontSize:'0.62rem', display:'inline-block', background:'rgba(148,163,184,0.1)', color:'#8a8278', border:'1px solid rgba(148,163,184,0.2)', borderRadius:'var(--radius)' }}>{'₹'}{it.rate} / {it.unit||'NOS'}</span>}
                  <span className="badge" style={{ fontSize:'0.62rem', display:'inline-block', background: isActive?'rgba(34,197,94,0.12)':'rgba(239,68,68,0.12)', color: isActive?'#4ade80':'#f87171', border:`1px solid ${isActive?'rgba(34,197,94,0.25)':'rgba(239,68,68,0.25)'}` }}>{isActive?'Active':'Inactive'}</span>
                </div>
              </div>
              <div style={{ display:'flex', gap:'0.5rem' }}>
                <button onClick={()=>startEdit(it)} style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem', background:'rgba(245,168,0,0.08)', border:'1px solid rgba(245,168,0,0.2)', borderRadius:'var(--radius)', color:'#f5a800', cursor:'pointer' }}>Edit</button>
                <button onClick={()=>toggleActive(it)} style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem', background: isActive?'rgba(148,163,184,0.08)':'rgba(34,197,94,0.08)', border:`1px solid ${isActive?'rgba(148,163,184,0.2)':'rgba(34,197,94,0.2)'}`, color: isActive?'#94a3b8':'#4ade80', cursor:'pointer' }}>{isActive?'Deactivate':'Activate'}</button>
                <button onClick={()=>del(it._id)} style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Delete</button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── MAINTENANCE MATERIALS ────────────────────────────────────────────────────
export function AdminMaintenanceMaterial() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ category:'bathroom', material:'' });
  const load = () => api.get('/maintenance-materials').then(r=>setItems(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);
  const resetForm = () => setForm({ category:'bathroom', material:'' });
  const del = async (id) => { if (!window.confirm('Delete?')) return undefined; await api.delete(`/maintenance-materials/${id}`); load(); toast.success('Deleted'); };
  const startEdit = (it) => { setEditingId(it._id); setForm({ category: it.category || 'bathroom', material: it.material || '' }); setShowForm(true); };
  const toggleActive = async (it) => {
    try { await api.patch(`/maintenance-materials/${it._id}`, { active: !(it.active !== false) }); load(); toast.success(it.active === false ? 'Marked active' : 'Marked inactive'); }
    catch { toast.error('Failed to update status'); }
  };
  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/maintenance-materials/${editingId}`, form);
        toast.success('Material updated!');
      } else {
        await api.post('/maintenance-materials', form);
        toast.success('Material added!');
      }
      setShowForm(false); setEditingId(null); resetForm(); load();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  return (
    <div>
      <AdminHeading eyebrow="Maintenance" title="Material" action={<button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>{ if(showForm){ resetForm(); setEditingId(null); } setShowForm(!showForm); }}>{showForm?'✕ Cancel':'+ Add Material'}</button>} />
      {showForm && (
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', marginBottom:'2rem' }}>
          <form onSubmit={handleSave}>
            <div className="grid-2">
              <div className="form-group">
                <label>Category</label>
                <select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))}>
                  {QUOTE_CATS.map(c=><option key={c} value={c}>{c.replace(/-/g,' ')}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Material</label><input value={form.material} onChange={e=>setForm(p=>({...p,material:e.target.value}))} placeholder="e.g. COM PLY CARCASS (FABRIC)" /></div>
            </div>
            <div style={{ display:'flex', gap:'0.75rem' }}>
              <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':(editingId?'Update':'Save')}</button>
              <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={()=>{ setShowForm(false); setEditingId(null); resetForm(); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
          {items.length===0 && <p style={{ color:'#8a8278', textAlign:'center', padding:'4rem' }}>No materials yet.</p>}
          {items.map(it=>{
            const isActive = it.active !== false;
            return (
            <div key={it._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', padding:'1.25rem', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.75rem', opacity: isActive?1:0.55 }}>
              <div>
                <h4 style={{ fontSize:'0.9rem', color:'#f8f5ef', fontWeight:500 }}>{it.material}</h4>
                <div style={{ display:'flex', gap:'0.4rem', marginTop:'0.3rem', flexWrap:'wrap' }}>
                  <span className="badge badge-blue" style={{ fontSize:'0.62rem', display:'inline-block' }}>{it.category?.replace(/-/g,' ')}</span>
                  <span className="badge" style={{ fontSize:'0.62rem', display:'inline-block', background: isActive?'rgba(34,197,94,0.12)':'rgba(239,68,68,0.12)', color: isActive?'#4ade80':'#f87171', border:`1px solid ${isActive?'rgba(34,197,94,0.25)':'rgba(239,68,68,0.25)'}` }}>{isActive?'Active':'Inactive'}</span>
                </div>
              </div>
              <div style={{ display:'flex', gap:'0.5rem' }}>
                <button onClick={()=>startEdit(it)} style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem', background:'rgba(245,168,0,0.08)', border:'1px solid rgba(245,168,0,0.2)', borderRadius:'var(--radius)', color:'#f5a800', cursor:'pointer' }}>Edit</button>
                <button onClick={()=>toggleActive(it)} style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem', background: isActive?'rgba(148,163,184,0.08)':'rgba(34,197,94,0.08)', border:`1px solid ${isActive?'rgba(148,163,184,0.2)':'rgba(34,197,94,0.2)'}`, color: isActive?'#94a3b8':'#4ade80', cursor:'pointer' }}>{isActive?'Deactivate':'Activate'}</button>
                <button onClick={()=>del(it._id)} style={{ padding:'0.32rem 0.65rem', fontSize:'0.68rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Delete</button>
              </div>
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── INVOICE (GST TAX INVOICE) ─────────────────────────────────────────────────
const emptyInvoiceItem = () => ({ description:'', hsn:'', qty:'', unit:'NOS', rate:'', amount:'' });
const emptyInvoiceCategory = (name = '') => ({ categoryName: name, items: [emptyInvoiceItem()] });

// Groups a flat invoice items array by their `category` field, preserving first-seen order.
// If none of the items carry a category (older invoices saved before grouping existed),
// returns a single group with categoryName:null so callers can skip the header row entirely.
const groupInvoiceItems = (items) => {
  const list = items || [];
  if (!list.some(it => it.category)) return [{ categoryName: null, items: list }];
  const groups = [];
  const byName = new Map();
  list.forEach(it => {
    const key = it.category || 'Other';
    if (!byName.has(key)) { const g = { categoryName: key, items: [] }; byName.set(key, g); groups.push(g); }
    byName.get(key).items.push(it);
  });
  return groups;
};

const cellS = { padding:'0.3rem 0.5rem', border:'1px solid #eee' };
const totLabel = { border:'1px solid #ccc', padding:'0.3rem 0.6rem', fontWeight:'bold' };
const totVal = { border:'1px solid #ccc', padding:'0.3rem 0.6rem', textAlign:'right' };

const fmtInvDate = (d) => {
  const dt = d ? new Date(d) : new Date();
  if (isNaN(dt.getTime())) return '-';
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${String(dt.getDate()).padStart(2,'0')}-${months[dt.getMonth()]}-${dt.getFullYear()}`;
};

const computeInvoiceTotals = (inv) => {
  const items = inv.items || [];
  const subTotal = items.reduce((s,it)=>s+(parseFloat(it.amount)||0),0);
  const totalQty = items.reduce((s,it)=>s+(parseFloat(it.qty)||0),0);
  const cgstPercent = parseFloat(inv.cgstPercent)||0;
  const sgstPercent = parseFloat(inv.sgstPercent)||0;
  const cgstAmt = subTotal*(cgstPercent/100);
  const sgstAmt = subTotal*(sgstPercent/100);
  const beforeRound = subTotal+cgstAmt+sgstAmt;
  const hasManualRound = inv.roundOff !== undefined && inv.roundOff !== null && inv.roundOff !== '';
  const roundOff = hasManualRound ? (parseFloat(inv.roundOff)||0) : Math.round((Math.round(beforeRound)-beforeRound)*100)/100;
  const grandTotal = Math.round((beforeRound+roundOff)*100)/100;
  const hsnMap = {};
  items.forEach(it=>{
    const key = it.hsn || '-';
    if (!hsnMap[key]) hsnMap[key] = { hsn:key, taxable:0 };
    hsnMap[key].taxable += parseFloat(it.amount)||0;
  });
  const hsnRows = Object.values(hsnMap).map(r=>({
    hsn: r.hsn,
    taxable: r.taxable,
    cgstAmt: r.taxable*(cgstPercent/100),
    sgstAmt: r.taxable*(sgstPercent/100),
    totalTax: r.taxable*(cgstPercent/100) + r.taxable*(sgstPercent/100),
  }));
  return { subTotal, totalQty, cgstPercent, sgstPercent, cgstAmt, sgstAmt, roundOff, grandTotal, hsnRows };
};

// Builds the invoice PDF. `settings` and `logoAsset` used to come from the
// component's state; they are parameters now so the preview page can supply
// its own copies without duplicating the layout.
export async function buildInvoicePDF(inv, variant = 'default', { settings = {}, logoAsset = null, signatureAsset = null } = {}) {
  const th = resolveTheme(variant);
  const isColor = variant==='color' || variant==='modern';
  const isEmerald = variant==='emerald';
  const isThemed = !['default','classic','color','modern','emerald'].includes(variant);
  const hasAccent = isColor || isEmerald || isThemed;
  const accentRGB = isColor ? [245,168,0] : isEmerald ? [5,150,105] : isThemed ? th.accent : [0,0,0];
  const headFillRGB = isColor ? [245,168,0] : isEmerald ? [17,24,39] : isThemed ? th.tableHead : [242,242,242];
  const headTextRGB = isColor ? [12,12,12] : isEmerald ? [255,255,255] : isThemed ? th.tableHeadInk : [0,0,0];
  const lineRGB = isColor ? [245,168,0] : isEmerald ? [5,150,105] : isThemed ? th.accent : [150,150,150];
  const navyRGB = isColor ? ((logoAsset && logoAsset.bg) ? [logoAsset.bg.r, logoAsset.bg.g, logoAsset.bg.b] : [15,64,74]) : isThemed ? th.ink : [0,0,0];
  const t = computeInvoiceTotals(inv);
  const commonUnit = (inv.items && inv.items[0] && inv.items[0].unit) || 'NOS';
  const doc = new jsPDF({ orientation:'portrait', unit:'pt', format:'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const paintPageBg = () => { if (isEmerald) { doc.setFillColor(236,253,245); doc.rect(0,0,pageWidth,pageHeight,'F'); } };
  paintPageBg();
  doc.setTextColor(0,0,0);
  let y = 40;
  let headerBottom = y;
  if (logoAsset) { headerBottom += 55*((logoAsset.height||158)/(logoAsset.width||650)) + 6; }
  headerBottom += 62;
  if (isColor) { doc.setFillColor(navyRGB[0],navyRGB[1],navyRGB[2]); doc.rect(0, 0, pageWidth, headerBottom, 'F'); }
  if (logoAsset) {
    try {
      const logoW = 55, logoH = logoW*((logoAsset.height||158)/(logoAsset.width||650));
      doc.addImage(logoAsset.dataUrl, 'PNG', pageWidth/2-logoW/2, y, logoW, logoH);
      y += logoH + 6;
    } catch {}
  }
  doc.setFontSize(13);
  doc.setFont(undefined,'bold');
  if (isColor) doc.setTextColor(accentRGB[0],accentRGB[1],accentRGB[2]); else doc.setTextColor(0,0,0);
  doc.text(settings.companyName||'Wedoo Interiors', pageWidth/2, y+10, { align:'center' });
  if (isColor) doc.setTextColor(255,255,255); else doc.setTextColor(0,0,0);
  doc.setFont(undefined,'normal');
  doc.setFontSize(8);
  doc.text(settings.address||'', pageWidth/2, y+24, { align:'center', maxWidth: pageWidth-100 });
  doc.text(`Email: ${settings.email||'-'}   GSTIN: ${settings.gstNo||'-'}`, pageWidth/2, y+38, { align:'center' });
  doc.text(`Ph: ${settings.phone||'-'}${settings.altPhone ? ', '+settings.altPhone : ''}`, pageWidth/2, y+50, { align:'center' });
  doc.setTextColor(0,0,0);
  y += 62;
  doc.setDrawColor(accentRGB[0],accentRGB[1],accentRGB[2]);
  doc.setLineWidth(1.2);
  doc.line(30, y, pageWidth-30, y);
  doc.setLineWidth(0.5);
  y += 20;
  doc.setFontSize(13);
  doc.setFont(undefined,'bold');
  if (hasAccent) doc.setTextColor(accentRGB[0],accentRGB[1],accentRGB[2]);
  doc.text('TAX INVOICE', pageWidth/2, y, { align:'center' });
  doc.setFont(undefined,'normal');
  doc.setTextColor(0,0,0);
  y += 10;
  doc.setDrawColor(accentRGB[0],accentRGB[1],accentRGB[2]);
  doc.setLineWidth(0.7);
  doc.line(30, y, pageWidth-30, y);
  y += 16;
  doc.setFontSize(8);
  const metaRows = [
    [`Invoice No. : ${inv.invoiceNo||'-'}`, `Reverse Charge : ${inv.reverseCharge||'No'}`, `Transport : ${inv.transport||'-'}`],
    [`Date : ${fmtInvDate(inv.invoiceDate)}`, `Vehicle No. : ${inv.vehicleNo||'-'}`, `P.O. No. : ${inv.poNo||'-'}`],
    [`Place of Supply : ${inv.placeOfSupply||'-'}`, `E-way Bill No. : ${inv.ewayBillNo||'-'}`, `Pymt Terms : ${inv.paymentTerms||'-'}`],
  ];
  const colW = (pageWidth-60)/3;
  metaRows.forEach((row,ri)=>{
    row.forEach((txt,ci)=>{ doc.text(txt, 30+ci*colW, y+ri*13, { maxWidth: colW-6 }); });
  });
  y += metaRows.length*13 + 8;
  doc.setDrawColor(accentRGB[0],accentRGB[1],accentRGB[2]);
  doc.setLineWidth(0.7);
  doc.line(30, y, pageWidth-30, y);
  y += 16;

  const ship = inv.shipSameAsBuyer ? { name:inv.buyerName, address:inv.buyerAddress, mobile:inv.buyerMobile, state:inv.buyerState, gstin:inv.buyerGSTIN } : { name:inv.shipName, address:inv.shipAddress, mobile:inv.shipMobile, state:inv.shipState, gstin:inv.shipGSTIN };
  const boxW = (pageWidth-60)/2;
  doc.setFontSize(8);
  const wrapLine = (txt) => doc.splitTextToSize(String(txt), boxW-16);
  const buyerLines = [inv.buyerName||'—', inv.buyerAddress||'', `Mobile: ${inv.buyerMobile||'-'}`, `State: ${inv.buyerState||'-'}`, `GSTIN: ${inv.buyerGSTIN||'-'}`].flatMap(wrapLine);
  const shipLines = [ship.name||'—', ship.address||'', `Mobile: ${ship.mobile||'-'}`, `State: ${ship.state||'-'}`, `GSTIN: ${ship.gstin||'-'}`].flatMap(wrapLine);
  const boxH = 28 + Math.max(buyerLines.length, shipLines.length)*11 + 8;
  doc.setDrawColor(lineRGB[0],lineRGB[1],lineRGB[2]);
  doc.rect(30, y, pageWidth-60, boxH);
  doc.line(30+boxW, y, 30+boxW, y+boxH);
  doc.setFontSize(8);
  doc.setFont(undefined,'bold');
  if (hasAccent) doc.setTextColor(accentRGB[0],accentRGB[1],accentRGB[2]);
  doc.text('Buyer :', 38, y+14);
  doc.text('Shipped To :', 38+boxW, y+14);
  doc.setTextColor(0,0,0);
  doc.setFont(undefined,'normal');
  buyerLines.forEach((l,i)=> doc.text(String(l), 38, y+28+i*11));
  shipLines.forEach((l,i)=> doc.text(String(l), 38+boxW, y+28+i*11));
  y += boxH + 18;

  const head = [['Sl','Item Description','HSN','Qty','Alt_Qty','Rate','Amount']];
  const body = [];
  { let sno = 0;
    groupInvoiceItems(inv.items).forEach(g => {
      if (g.categoryName) {
        body.push([{ content: g.categoryName, colSpan:7, styles:{ fontStyle:'bold', fillColor: isColor?[255,244,222]:isEmerald?[220,252,231]:[250,245,230], textColor: hasAccent?accentRGB:[0,0,0] } }]);
      }
      g.items.forEach(it => { sno++; body.push([sno, it.description||'—', it.hsn||'-', `${it.qty||'-'} ${it.unit||'NOS'}`, '', fmtAmt(it.rate), fmtAmt(it.amount)]); });
    });
  }
  body.push([{ content:'', colSpan:6, styles:{ halign:'right', fontStyle:'bold' } }, { content: fmtAmt(t.subTotal), styles:{ halign:'right', fontStyle:'bold' } }]);
  body.push([{ content:`SGST OUTPUT ${t.sgstPercent}%`, colSpan:6, styles:{ halign:'right' } }, { content: fmtAmt(t.sgstAmt), styles:{ halign:'right' } }]);
  body.push([{ content:`CGST OUTPUT ${t.cgstPercent}%`, colSpan:6, styles:{ halign:'right' } }, { content: fmtAmt(t.cgstAmt), styles:{ halign:'right' } }]);
  body.push([{ content:'Round Off', colSpan:6, styles:{ halign:'right' } }, { content: fmtAmt(t.roundOff), styles:{ halign:'right' } }]);
  body.push([
    '', { content:'GRAND TOTAL', styles:{ fontStyle:'bold', fillColor: isColor?[255,244,222]: isEmerald?[220,252,231]:false, textColor: isEmerald?accentRGB:undefined } }, '',
    { content:`${t.totalQty} ${commonUnit}`, styles:{ fontStyle:'bold', fillColor: isColor?[255,244,222]: isEmerald?[220,252,231]:false, textColor: isEmerald?accentRGB:undefined } }, '', '',
    { content: fmtAmt(t.grandTotal), styles:{ halign:'right', fontStyle:'bold', fillColor: isColor?[255,244,222]: isEmerald?[220,252,231]:false, textColor: isEmerald?accentRGB:undefined } },
  ]);
  autoTable(doc, {
    head, body, startY: y,
    styles: { fontSize: 8, lineColor: lineRGB, lineWidth: 0.5 },
    headStyles: { fillColor: headFillRGB, textColor: headTextRGB, lineColor: lineRGB, lineWidth: 0.5 },
    margin: { left: 30, right: 30 },
  });
  y = doc.lastAutoTable.finalY + 20;
  if (y > 660) { doc.addPage(); paintPageBg(); y = 40; }

  doc.setDrawColor(hasAccent?accentRGB[0]:180, hasAccent?accentRGB[1]:180, hasAccent?accentRGB[2]:180);
  doc.line(30, y, pageWidth-30, y);
  doc.setFontSize(8.5);
  doc.setFont(undefined,'normal');
  const wordsLines = doc.splitTextToSize(`Amount in Words : INR ${numberToWordsIndian(t.grandTotal)}`, pageWidth-120);
  doc.text(wordsLines, 30, y+14);
  doc.setFontSize(7.5);
  doc.setFont(undefined,'italic');
  doc.text('E & O.E', pageWidth-30, y+14, { align:'right' });
  doc.setFont(undefined,'normal');
  doc.setFontSize(8.5);
  y += 14 + wordsLines.length*11 + 6;
  doc.line(30, y, pageWidth-30, y);
  y += 20;

  const hsnHead = [[{ content:'HSN/SAC', rowSpan:2 }, { content:'Taxable Value', rowSpan:2 }, { content:'CGST', colSpan:2 }, { content:'SGST/UTGST', colSpan:2 }, { content:'Total Tax Amount', rowSpan:2 }], ['Rate','Amount','Rate','Amount']];
  const hsnBody = t.hsnRows.map(r=>[r.hsn, fmtAmt(r.taxable), `${t.cgstPercent}%`, fmtAmt(r.cgstAmt), `${t.sgstPercent}%`, fmtAmt(r.sgstAmt), fmtAmt(r.totalTax)]);
  hsnBody.push(['Total', fmtAmt(t.subTotal), '', fmtAmt(t.cgstAmt), '', fmtAmt(t.sgstAmt), fmtAmt(t.cgstAmt+t.sgstAmt)]);
  autoTable(doc, {
    head: hsnHead, body: hsnBody, startY: y,
    styles: { fontSize: 7.5, lineColor: lineRGB, lineWidth: 0.5 },
    headStyles: { fillColor: headFillRGB, textColor: headTextRGB, lineColor: lineRGB, lineWidth: 0.5, halign:'center' },
    margin: { left: 30, right: 30 },
  });
  y = doc.lastAutoTable.finalY + 20;
  if (y > 660) { doc.addPage(); paintPageBg(); y = 40; }

  doc.setFontSize(8);
  if (settings.bankAccountNo || settings.bankName) {
    const bankColCenterX = pageWidth/2;
    doc.setFont(undefined,'bold');
    if (hasAccent) doc.setTextColor(accentRGB[0],accentRGB[1],accentRGB[2]);
    doc.text("Co's Bank Details :", bankColCenterX, y, { align:'center' });
    doc.setTextColor(0,0,0);
    doc.setFont(undefined,'normal');
    const bankLines = [
      `A/c Holder's Nm : ${settings.bankAccountHolder||settings.companyName||'-'}`,
      `A/c No. : ${settings.bankAccountNo||'-'}`,
      `Bank Name : ${settings.bankName||'-'}`,
      `Branch : ${settings.bankBranch||'-'}`,
      `IFS Code : ${settings.bankIFSC||'-'}`,
    ];
    const bankBlockX = bankColCenterX - Math.max(...bankLines.map(l=>doc.getTextWidth(l)))/2;
    bankLines.forEach((l,i)=> doc.text(l, bankBlockX, y+14+i*11));
    y += 14 + bankLines.length*11 + 10;
  }
  doc.setFont(undefined,'bold');
  doc.text('Declaration:', 30, y);
  doc.setFont(undefined,'normal');
  const declLines = doc.splitTextToSize('We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.', 320);
  doc.text(declLines, 30, y+12);
  let declY = y+12+declLines.length*10;
  doc.text('* NO RETURN & NO EXCHANGE', 30, declY+8);
  doc.text('* We are not responsible for any breakage/damage/shortage/leakage in transit.', 30, declY+18, { maxWidth: 320 });

  const sigX = pageWidth-160;
  doc.setFontSize(8);
  doc.text(`For ${settings.companyName||'Wedoo Interiors'}`, sigX, y);
  if (signatureAsset) {
    try {
      const sigW=90, sigH= sigW*((signatureAsset.height||40)/(signatureAsset.width||150));
      doc.addImage(signatureAsset.dataUrl, 'PNG', sigX, y+30-sigH, sigW, sigH);
    } catch {}
  }
  doc.setDrawColor(hasAccent?accentRGB[0]:60, hasAccent?accentRGB[1]:60, hasAccent?accentRGB[2]:60);
  doc.line(sigX, y+36, sigX+90, y+36);
  doc.text('Authorized Signatory', sigX+45, y+48, { align:'center' });

  return doc;
}

export async function downloadInvoiceWord(inv, variant = 'default', { settings = {}, logoAsset = null, signatureAsset = null } = {}) {
  const th = resolveTheme(variant);
  const isColor = variant==='color' || variant==='modern';
  const isEmerald = variant==='emerald';
  const isThemed = !['default','classic','color','modern','emerald'].includes(variant);
  const hasAccent = isColor || isEmerald || isThemed;
  const accent = isColor ? '#F5A800' : isEmerald ? '#059669' : isThemed ? rgb(th.accent) : '#000';
  const headBg = isColor ? '#F5A800' : isEmerald ? '#111827' : isThemed ? rgb(th.tableHead) : '#f2f2f2';
  const headTextColor = isColor ? '#0c0c0c' : isEmerald ? '#fff' : isThemed ? rgb(th.tableHeadInk) : '#000';
  const lineColor = isColor ? '#F5A800' : isEmerald ? '#059669' : isThemed ? rgb(th.accent) : '#999';
  const navy = isColor ? ((logoAsset && logoAsset.bg) ? `rgb(${logoAsset.bg.r},${logoAsset.bg.g},${logoAsset.bg.b})` : '#0f404a') : isThemed ? rgb(th.ink) : '#000';
  const t = computeInvoiceTotals(inv);
  const ship = inv.shipSameAsBuyer ? { name:inv.buyerName, address:inv.buyerAddress, mobile:inv.buyerMobile, state:inv.buyerState, gstin:inv.buyerGSTIN } : { name:inv.shipName, address:inv.shipAddress, mobile:inv.shipMobile, state:inv.shipState, gstin:inv.shipGSTIN };
  const commonUnit = (inv.items && inv.items[0] && inv.items[0].unit) || 'NOS';
  const itemsHtml = (() => {
    let sno = 0;
    return groupInvoiceItems(inv.items).map(g => {
      const header = g.categoryName
        ? `<tr><td colspan="7" style="font-weight:bold;background:${isColor?'#fff4de':isEmerald?'#dcfce7':'#faf5e6'};color:${hasAccent?accent:'#000'};">${g.categoryName}</td></tr>`
        : '';
      const rows = g.items.map(it => { sno++; return `
    <tr>
      <td>${sno}</td><td>${it.description||'—'}</td><td>${it.hsn||'-'}</td><td>${it.qty||'-'} ${it.unit||'NOS'}</td><td></td><td>${fmtAmt(it.rate)}</td><td>${fmtAmt(it.amount)}</td>
    </tr>`; }).join('');
      return header + rows;
    }).join('');
  })();
  const hsnHtml = t.hsnRows.map(r=>`
    <tr><td>${r.hsn}</td><td>${fmtAmt(r.taxable)}</td><td>${t.cgstPercent}%</td><td>${fmtAmt(r.cgstAmt)}</td><td>${t.sgstPercent}%</td><td>${fmtAmt(r.sgstAmt)}</td><td>${fmtAmt(r.totalTax)}</td></tr>
  `).join('');
  const html = `
    <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
    <head><meta charset="utf-8"><title>Invoice</title></head>
    <body style="font-family:Arial,sans-serif;color:#000;font-size:11px;background-color:${isEmerald?'#ecfdf5':'#fff'};">
      <div style="text-align:center;background:${isColor?navy:'transparent'};margin:${isColor?'0 0 6px':'0 0 6px'};padding:${isColor?'14px 16px 12px':'0 0 8px'};border-bottom:2px solid ${accent};">
        ${logoAsset ? `<img src="${logoAsset.dataUrl}" width="60" style="display:block;margin:0 auto 6px;" />` : ''}
        <div style="font-size:14px;font-weight:bold;color:${isColor?accent:navy};">${settings.companyName||'Wedoo Interiors'}</div>
        <div style="font-size:10px;color:${isColor?'rgba(255,255,255,0.92)':'inherit'};">${settings.address||''}</div>
        <div style="font-size:10px;color:${isColor?'rgba(255,255,255,0.92)':'inherit'};">Email: ${settings.email||'-'} &nbsp; GSTIN: ${settings.gstNo||'-'}</div>
        <div style="font-size:10px;color:${isColor?'rgba(255,255,255,0.92)':'inherit'};">Ph: ${settings.phone||'-'}${settings.altPhone ? ', '+settings.altPhone : ''}</div>
      </div>
      <h2 style="text-align:center;letter-spacing:2px;font-size:15px;border-bottom:1px solid ${accent};padding-bottom:6px;margin-top:0;color:${hasAccent?accent:'#000'};">TAX INVOICE</h2>
      <table cellpadding="6" cellspacing="0" style="border-collapse:collapse;width:100%;font-size:10.5px;border-bottom:1px solid ${accent};">
        <tr><td><b>Invoice No.</b> : ${inv.invoiceNo||'-'}</td><td><b>Reverse Charge</b> : ${inv.reverseCharge||'No'}</td><td><b>Transport</b> : ${inv.transport||'-'}</td></tr>
        <tr><td><b>Date</b> : ${fmtInvDate(inv.invoiceDate)}</td><td><b>Vehicle No.</b> : ${inv.vehicleNo||'-'}</td><td><b>P.O. No.</b> : ${inv.poNo||'-'}</td></tr>
        <tr><td><b>Place of Supply</b> : ${inv.placeOfSupply||'-'}</td><td><b>E-way Bill No.</b> : ${inv.ewayBillNo||'-'}</td><td><b>Pymt Terms</b> : ${inv.paymentTerms||'-'}</td></tr>
      </table>
      <br/>
      <table width="100%" border="1" style="border-collapse:collapse;border-color:${lineColor};"><tr>
        <td width="50%" valign="top" style="padding:8px;">
          <div style="font-weight:bold;color:${hasAccent?accent:'#000'};">Buyer :</div>
          <div style="font-weight:bold;">${inv.buyerName||'—'}</div>
          <div>${inv.buyerAddress||''}</div>
          <div>Mobile: ${inv.buyerMobile||'-'}</div>
          <div>State: ${inv.buyerState||'-'}</div>
          <div>GSTIN: ${inv.buyerGSTIN||'-'}</div>
        </td>
        <td width="50%" valign="top" style="padding:8px;">
          <div style="font-weight:bold;color:${hasAccent?accent:'#000'};">Shipped To :</div>
          <div style="font-weight:bold;">${ship.name||'—'}</div>
          <div>${ship.address||''}</div>
          <div>Mobile: ${ship.mobile||'-'}</div>
          <div>State: ${ship.state||'-'}</div>
          <div>GSTIN: ${ship.gstin||'-'}</div>
        </td>
      </tr></table>
      <br/>
      <table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse;width:100%;font-size:10px;border-color:${lineColor};">
        <thead><tr style="background:${headBg};">
          ${['Sl','Item Description','HSN','Qty','Alt_Qty','Rate','Amount'].map(h=>`<th style="padding:4px;text-align:left;color:${headTextColor};">${h}</th>`).join('')}
        </tr></thead>
        <tbody>
          ${itemsHtml}
          <tr><td colspan="6" style="text-align:right;border-top:2px solid ${accent};font-weight:bold;padding:4px;">&nbsp;</td><td style="text-align:right;border-top:2px solid ${accent};font-weight:bold;padding:4px;">${fmtAmt(t.subTotal)}</td></tr>
          <tr><td colspan="6" style="text-align:right;padding:4px;">SGST OUTPUT ${t.sgstPercent}%</td><td style="text-align:right;padding:4px;">${fmtAmt(t.sgstAmt)}</td></tr>
          <tr><td colspan="6" style="text-align:right;padding:4px;">CGST OUTPUT ${t.cgstPercent}%</td><td style="text-align:right;padding:4px;">${fmtAmt(t.cgstAmt)}</td></tr>
          <tr><td colspan="6" style="text-align:right;padding:4px;">Round Off</td><td style="text-align:right;padding:4px;">${fmtAmt(t.roundOff)}</td></tr>
          <tr style="border-top:2px solid ${accent};font-weight:bold;background:${isColor?'#fff4de':isEmerald?'#dcfce7':'transparent'};color:${isEmerald?accent:'inherit'};"><td style="padding:4px;"></td><td style="padding:4px;">GRAND TOTAL</td><td style="padding:4px;"></td><td style="padding:4px;">${t.totalQty} ${commonUnit}</td><td style="padding:4px;"></td><td style="padding:4px;"></td><td style="text-align:right;padding:4px;">${fmtAmt(t.grandTotal)}</td></tr>
        </tbody>
      </table>
      <br/>
      <table width="100%" style="border-top:1px solid ${hasAccent?accent:'#ccc'};border-bottom:1px solid ${hasAccent?accent:'#ccc'};"><tr>
        <td style="padding:6px 0;font-size:10.5px;">Amount in Words : INR ${numberToWordsIndian(t.grandTotal)}</td>
        <td style="padding:6px 0;font-size:9px;font-style:italic;text-align:right;">E &amp; O.E</td>
      </tr></table>
      <br/>
      <table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse;width:100%;font-size:9.5px;border-color:${lineColor};">
        <thead>
          <tr style="background:${headBg};">
            <th rowspan="2" style="padding:4px;color:${headTextColor};">HSN/SAC</th>
            <th rowspan="2" style="padding:4px;color:${headTextColor};">Taxable Value</th>
            <th colspan="2" style="padding:4px;color:${headTextColor};">CGST</th>
            <th colspan="2" style="padding:4px;color:${headTextColor};">SGST/UTGST</th>
            <th rowspan="2" style="padding:4px;color:${headTextColor};">Total Tax Amount</th>
          </tr>
          <tr style="background:${headBg};">
            <th style="padding:4px;color:${headTextColor};">Rate</th><th style="padding:4px;color:${headTextColor};">Amount</th>
            <th style="padding:4px;color:${headTextColor};">Rate</th><th style="padding:4px;color:${headTextColor};">Amount</th>
          </tr>
        </thead>
        <tbody>
          ${hsnHtml}
          <tr style="font-weight:bold;"><td>Total</td><td>${fmtAmt(t.subTotal)}</td><td></td><td>${fmtAmt(t.cgstAmt)}</td><td></td><td>${fmtAmt(t.sgstAmt)}</td><td>${fmtAmt(t.cgstAmt+t.sgstAmt)}</td></tr>
        </tbody>
      </table>
      <br/>
      <table width="100%">
        ${(settings.bankAccountNo || settings.bankName) ? `
        <tr><td colspan="2" style="text-align:center;font-size:10px;padding-bottom:10px;">
          <div style="font-weight:bold;margin-bottom:4px;color:${hasAccent?accent:'#000'};">Co's Bank Details :</div>
          <table align="center" style="margin:0 auto;"><tr><td style="text-align:left;">
            <div>A/c Holder's Nm : ${settings.bankAccountHolder||settings.companyName||'-'}</div>
            <div>A/c No. : ${settings.bankAccountNo||'-'}</div>
            <div>Bank Name : ${settings.bankName||'-'}</div>
            <div>Branch : ${settings.bankBranch||'-'}</div>
            <div>IFS Code : ${settings.bankIFSC||'-'}</div>
          </td></tr></table>
        </td></tr>
        ` : ''}
      <tr>
        <td width="60%" valign="top" style="font-size:10px;">
          <div style="margin-top:10px;">
            <div style="font-weight:bold;">Declaration:</div>
            <div>We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.</div>
            <div style="margin-top:4px;">* NO RETURN &amp; NO EXCHANGE</div>
            <div>* We are not responsible for any breakage/damage/shortage/leakage in transit.</div>
          </div>
        </td>
        <td width="40%" valign="top" style="text-align:center;">
          <div style="font-size:10px;">For ${settings.companyName||'Wedoo Interiors'}</div>
          ${signatureAsset ? `<img src="${signatureAsset.dataUrl}" style="height:40px;display:block;margin:8px auto 4px;" />` : '<br/><br/>'}
          <div style="border-top:1px solid ${hasAccent?accent:'#333'};padding-top:4px;font-size:10px;">Authorized Signatory</div>
        </td>
      </tr>
      </table>
    </body>
    </html>`;
  const blob = new Blob(['﻿', html], { type:'application/msword;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `invoice-${(inv.invoiceNo||'draft').replace(/[\/\s]/g,'-')}.doc`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [itemOptions, setItemOptions] = useState([]);
  const [quotesHistory, setQuotesHistory] = useState([]);
  const [settings, setSettings] = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [logoAsset, setLogoAsset] = useState(null);
  const [signatureAsset, setSignatureAsset] = useState(null);
  const [shareMenuFor, setShareMenuFor] = useState(null); // { id, top, left } of the open share dropdown, or null

  const blankForm = () => ({
    invoiceNo: `WD/${new Date().getFullYear()}/${String(Date.now()).slice(-4)}`,
    invoiceDate: new Date().toISOString().slice(0,10),
    reverseCharge: 'No', transport:'', vehicleNo:'', poNo:'', placeOfSupply:'', ewayBillNo:'', paymentTerms:'',
    buyerName:'', buyerAddress:'', buyerMobile:'', buyerState:'', buyerGSTIN:'',
    shipSameAsBuyer: true, shipName:'', shipAddress:'', shipMobile:'', shipState:'', shipGSTIN:'',
    cgstPercent: 9, sgstPercent: 9, roundOff: '',
  });
  const [form, setForm] = useState(blankForm());
  const [categories, setCategories] = useState([emptyInvoiceCategory()]);

  const load = () => api.get('/invoices').then(r=>setInvoices(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);
  useEffect(()=>{
    api.get('/maintenance-items').then(r=>setItemOptions(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{});
    api.get('/quotes').then(r=>setQuotesHistory(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(()=>{});
    api.get('/settings').then(r=>{ setSettings(r.data||{}); setSettingsLoaded(true); }).catch(()=>setSettingsLoaded(true));
  },[]);
  useEffect(()=>{
    if (!settingsLoaded) return;
    let cancelled = false;
    loadImageAsset(settings.logo ? imgSrc(settings.logo) : quoteLogo, { sampleBg:true }).then(d=>{ if (cancelled) return; if (d) setLogoAsset(d); });
    return () => { cancelled = true; };
  },[settingsLoaded, settings.logo]);
  useEffect(()=>{
    if (!settingsLoaded || !settings.signature) { setSignatureAsset(null); return; }
    let cancelled = false;
    loadImageAsset(imgSrc(settings.signature)).then(d=>{ if (!cancelled) setSignatureAsset(d); });
    return () => { cancelled = true; };
  },[settingsLoaded, settings.signature]);

  const resetForm = () => { setForm(blankForm()); setCategories([emptyInvoiceCategory()]); };

  const findLatestQuoteFor = (itemName) => {
    const target = (itemName||'').trim().toLowerCase();
    if (!target) return null;
    const matches = quotesHistory.filter(q => (q.itemSpec||'').trim().toLowerCase() === target);
    if (matches.length === 0) return null;
    matches.sort((a,b) => new Date(b.updatedAt||b.createdAt||0) - new Date(a.updatedAt||a.createdAt||0));
    return matches[0];
  };
  const updateItem = (ci, ii, field, value) => {
    setCategories(prev => prev.map((cat, c) => {
      if (c!==ci) return cat;
      return { ...cat, items: cat.items.map((it,i) => {
        if (i!==ii) return it;
        const next = { ...it, [field]: value };
        if (field==='qty' || field==='rate') next.amount = ((parseFloat(next.qty)||0)*(parseFloat(next.rate)||0)).toFixed(2);
        if (field==='description') {
          const match = itemOptions.find(o=>o.item===value);
          next.hsn = match?.hsn || '';
          next.unit = match?.unit || 'NOS';
          const lastQuote = findLatestQuoteFor(value);
          if (lastQuote) {
            next.qty = lastQuote.qty!=null && lastQuote.qty!=='' ? String(lastQuote.qty) : next.qty;
            next.rate = lastQuote.unitSft!=null && lastQuote.unitSft!=='' ? String(lastQuote.unitSft) : (match?.rate!=null ? String(match.rate) : '');
            next.amount = lastQuote.amount!=null && lastQuote.amount!=='' ? parseFloat(lastQuote.amount).toFixed(2) : ((parseFloat(next.qty)||0)*(parseFloat(next.rate)||0)).toFixed(2);
          } else {
            next.rate = match?.rate!=null ? String(match.rate) : '';
            next.amount = ((parseFloat(next.qty)||0)*(parseFloat(next.rate)||0)).toFixed(2);
          }
        }
        return next;
      }) };
    }));
  };
  const addCategory = () => setCategories(prev => [...prev, emptyInvoiceCategory()]);
  const removeCategory = (ci) => setCategories(prev => prev.length>1 ? prev.filter((_,i)=>i!==ci) : prev);
  const updateCategoryName = (ci, name) => setCategories(prev => prev.map((c,i)=> i===ci ? { ...c, categoryName:name } : c));
  const addItemRow = (ci) => setCategories(prev => prev.map((c,i)=> i===ci ? { ...c, items:[...c.items, emptyInvoiceItem()] } : c));
  const removeItemRow = (ci, ii) => setCategories(prev => prev.map((c,i)=> {
    if (i!==ci) return c;
    return { ...c, items: c.items.length>1 ? c.items.filter((_,j)=>j!==ii) : c.items };
  }));

  const del = async (id) => { if (!window.confirm('Delete?')) return undefined; await api.delete(`/invoices/${id}`); load(); toast.success('Deleted'); };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const flatItems = categories.flatMap(cat =>
        cat.items
          .filter(it => it.description || it.amount)
          .map(it => ({ ...it, category: cat.categoryName || '' }))
      );
      const payload = { ...form, items: flatItems };
      if (!isValidOptionalPhone(form.buyerMobile)) { toast.error(PHONE_ERROR); setSaving(false); return; }
      await api.post('/invoices', payload);
      toast.success('Invoice saved!'); setShowForm(false); resetForm(); load();
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  // ─── document renderer (shared by preview + print) ───────────────────────


  const downloadInvoicePDF = async (inv, variant='default') => {
    const doc = await buildInvoicePDF(inv, variant, { settings, logoAsset, signatureAsset });
    doc.save(`invoice-${(inv.invoiceNo||'draft').replace(/[\/\s]/g,'-')}.pdf`);
  };

  // Shares an invoice via WhatsApp or Email. Where the browser supports the
  // native Web Share API with files, this hands the PDF straight to the OS
  // share sheet — the user taps WhatsApp/Mail and the file is already
  // attached. Falls back to downloading the PDF and opening a pre-filled
  // WhatsApp/email link on browsers that can't share files.
  const shareInvoice = async (inv, channel, variant='default') => {
    setShareMenuFor(null);
    let doc = null;
    try {
      doc = await buildInvoicePDF(inv, variant, { settings, logoAsset, signatureAsset });
    } catch { toast.error('Could not generate PDF'); }
    if (!doc) return;

    const t = computeInvoiceTotals(inv);
    const message = `Hi ${inv.buyerName || 'there'}, here is your invoice ${inv.invoiceNo || ''} — Grand Total: ₹ ${fmtAmt(t.grandTotal)}. - Wedoo Interiors`;

    try {
      const blob = doc.output('blob');
      const file = new File([blob], `invoice-${(inv.invoiceNo||'draft').replace(/[\/\s]/g,'-')}.pdf`, { type: 'application/pdf' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: `Invoice ${inv.invoiceNo||''}`, text: message });
        return;
      }
    } catch (err) {
      if (err && err.name === 'AbortError') return;
    }

    doc.save(`invoice-${(inv.invoiceNo||'draft').replace(/[\/\s]/g,'-')}.pdf`);
    toast('PDF downloaded — please attach it in the chat/email that just opened.');
    if (channel === 'whatsapp') {
      const digits = (inv.buyerMobile || '').replace(/[^0-9]/g, '');
      const phone = digits ? (digits.length === 10 ? `91${digits}` : digits) : '';
      const params = new URLSearchParams();
      if (phone) params.set('phone', phone);
      params.set('text', `${message} The PDF has just been downloaded on this device — please attach it here.`);
      window.open(`https://api.whatsapp.com/send?${params.toString()}`, '_blank');
    } else if (channel === 'email') {
      const subject = `Invoice ${inv.invoiceNo||''} - Wedoo Interiors`;
      const body = `${message} The PDF has just been downloaded on this device — please attach it here.`;
      window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }
  };


  const sectionH = { color:'#F5A800', fontSize:'0.78rem', margin:'1.25rem 0 0.75rem', textTransform:'uppercase', letterSpacing:'0.04em' };
  const rowInput = { fontSize:'0.7rem', padding:'0.3rem', background:'#0c0c0c', border:'1px solid rgba(255,255,255,0.1)', color:'#ede8de', width:'100%' };

  return (
    <div>
      <AdminHeading eyebrow="Operations" title="Invoice" action={<button className="btn btn-gold" style={{ fontSize:'0.72rem' }} onClick={()=>{ if(showForm) resetForm(); setShowForm(!showForm); }}>{showForm?'✕ Cancel':'+ Add Invoice'}</button>} />
      {showForm && (
        <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', marginBottom:'2rem' }}>
          <form onSubmit={handleSave}>
            <h4 style={sectionH}>Invoice Details</h4>
            <div className="grid-2">
              <div className="form-group"><label>Invoice No.</label><input value={form.invoiceNo} onChange={e=>setForm(p=>({...p,invoiceNo:e.target.value}))} /></div>
              <div className="form-group"><label>Invoice Date</label><input type="date" value={form.invoiceDate} onChange={e=>setForm(p=>({...p,invoiceDate:e.target.value}))} /></div>
              <div className="form-group"><label>Reverse Charge</label>
                <select value={form.reverseCharge} onChange={e=>setForm(p=>({...p,reverseCharge:e.target.value}))}>
                  <option value="No">No</option><option value="Yes">Yes</option>
                </select>
              </div>
              <div className="form-group"><label>Payment Terms</label><input value={form.paymentTerms} onChange={e=>setForm(p=>({...p,paymentTerms:e.target.value}))} placeholder="e.g. 60 days" /></div>
              <div className="form-group"><label>Transport</label><input value={form.transport} onChange={e=>setForm(p=>({...p,transport:e.target.value}))} /></div>
              <div className="form-group"><label>Vehicle No.</label><input value={form.vehicleNo} onChange={e=>setForm(p=>({...p,vehicleNo:e.target.value}))} /></div>
              <div className="form-group"><label>P.O. No.</label><input value={form.poNo} onChange={e=>setForm(p=>({...p,poNo:e.target.value}))} /></div>
              <div className="form-group"><label>Place of Supply</label><input value={form.placeOfSupply} onChange={e=>setForm(p=>({...p,placeOfSupply:e.target.value}))} placeholder="e.g. Karnataka(29)" /></div>
              <div className="form-group"><label>E-way Bill No.</label><input value={form.ewayBillNo} onChange={e=>setForm(p=>({...p,ewayBillNo:e.target.value}))} /></div>
            </div>

            <h4 style={sectionH}>Buyer</h4>
            <div className="grid-2">
              <div className="form-group"><label>Buyer Name</label><input value={form.buyerName} onChange={e=>setForm(p=>({...p,buyerName:e.target.value}))} /></div>
              <div className="form-group"><label>Mobile</label><input {...phoneInputProps} value={form.buyerMobile} onChange={e=>setForm(p=>({...p,buyerMobile:sanitizePhone(e.target.value)}))} /></div>
              <div className="form-group"><label>State</label><input value={form.buyerState} onChange={e=>setForm(p=>({...p,buyerState:e.target.value}))} placeholder="e.g. Karnataka(29)" /></div>
              <div className="form-group"><label>GSTIN</label><input value={form.buyerGSTIN} onChange={e=>setForm(p=>({...p,buyerGSTIN:e.target.value.toUpperCase()}))} /></div>
            </div>
            <div className="form-group"><label>Address</label><textarea rows={2} value={form.buyerAddress} onChange={e=>setForm(p=>({...p,buyerAddress:e.target.value}))} /></div>

            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', margin:'1.25rem 0 0.75rem' }}>
              <h4 style={{...sectionH, margin:0}}>Items</h4>
              <button type="button" onClick={addCategory} style={{ padding:'0.3rem 0.7rem', fontSize:'0.68rem', background:'#F5A800', border:'none', color:'#0c0c0c', fontWeight:600, cursor:'pointer' }}>+ Add Category</button>
            </div>
            <div style={{ overflowX:'auto', marginBottom:'0.5rem' }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.75rem' }}>
                <thead>
                  <tr style={{ background:'#F5A800' }}>
                    {['Item / Description','HSN','Qty','Unit','Rate','Amount',''].map(h=>
                      <th key={h} style={{ padding:'0.4rem 0.5rem', textAlign:'left', color:'#0c0c0c', fontWeight:600 }}>{h}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {categories.map((cat, ci) => (
                    <React.Fragment key={ci}>
                      <tr>
                        <td colSpan={7} style={{ padding:'0.5rem', background:'rgba(245,168,0,0.08)' }}>
                          <div style={{ display:'flex', gap:'0.75rem', alignItems:'center' }}>
                            <select value={cat.categoryName||''} onChange={e=>updateCategoryName(ci,e.target.value)}
                              style={{ background:'#0c0c0c', border:'1px solid rgba(245,168,0,0.3)', borderRadius:'var(--radius)', color:'#F5A800', fontWeight:600, padding:'0.35rem 0.5rem', fontSize:'0.75rem' }}>
                              <option value="">Category name...</option>
                              {QUOTE_CATS.map(c=><option key={c} value={c}>{c.replace(/-/g,' ')}</option>)}
                            </select>
                            {categories.length>1 && <button type="button" onClick={()=>removeCategory(ci)} style={{ padding:'0.2rem 0.5rem', fontSize:'0.65rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>✕ Remove Category</button>}
                          </div>
                        </td>
                      </tr>
                      {cat.items.map((it,ii)=>(
                        <tr key={ii}>
                          <td style={{ padding:'0.3rem' }}>
                            <select value={it.description||''} onChange={e=>updateItem(ci,ii,'description',e.target.value)} style={{...rowInput,minWidth:'160px'}}>
                              <option value="">Select item...</option>
                              {itemOptions.map(o=><option key={o._id} value={o.item}>{o.item}</option>)}
                            </select>
                          </td>
                          <td style={{ padding:'0.3rem' }}><input value={it.hsn||''} onChange={e=>updateItem(ci,ii,'hsn',e.target.value.replace(/[^0-9]/g,''))} placeholder="HSN" style={{...rowInput,width:'75px'}} /></td>
                          <td style={{ padding:'0.3rem' }}><input value={it.qty||''} onChange={e=>updateItem(ci,ii,'qty',e.target.value.replace(/[^0-9.]/g,''))} placeholder="Qty" style={{...rowInput,width:'55px'}} /></td>
                          <td style={{ padding:'0.3rem' }}><input value={it.unit||'NOS'} onChange={e=>updateItem(ci,ii,'unit',e.target.value)} placeholder="NOS" style={{...rowInput,width:'55px'}} /></td>
                          <td style={{ padding:'0.3rem' }}><input value={it.rate||''} onChange={e=>updateItem(ci,ii,'rate',e.target.value.replace(/[^0-9.]/g,''))} placeholder="Rate" style={{...rowInput,width:'75px'}} /></td>
                          <td style={{ padding:'0.3rem', color:'#F5A800', fontWeight:600 }}>{fmtAmt(it.amount||0)}</td>
                          <td style={{ padding:'0.3rem' }}>{cat.items.length>1 && <button type="button" onClick={()=>removeItemRow(ci,ii)} style={{ padding:'0.2rem 0.5rem', fontSize:'0.65rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>✕</button>}</td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={7} style={{ padding:'0.4rem 0.3rem' }}>
                          <button type="button" onClick={()=>addItemRow(ci)} style={{ padding:'0.3rem 0.7rem', fontSize:'0.68rem', background:'rgba(245,168,0,0.08)', border:'1px dashed rgba(245,168,0,0.3)', color:'#F5A800', cursor:'pointer' }}>+ Add Item</button>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            {categories.some(cat=>cat.items.some(it=>it.description && !it.rate)) && (
              <p style={{ fontSize:'0.68rem', color:'#f5a800', marginTop:0, marginBottom:'0.5rem' }}>
                ⚠ Some items have no saved rate — type one in the Rate column for this invoice, or add it once in Maintenance → Item so it auto-fills next time.
              </p>
            )}

            <h4 style={sectionH}>Tax</h4>
            <div className="grid-2">
              <div className="form-group"><label>CGST %</label><input value={form.cgstPercent} onChange={e=>setForm(p=>({...p,cgstPercent:e.target.value.replace(/[^0-9.]/g,'')}))} /></div>
              <div className="form-group"><label>SGST %</label><input value={form.sgstPercent} onChange={e=>setForm(p=>({...p,sgstPercent:e.target.value.replace(/[^0-9.]/g,'')}))} /></div>
            </div>
            <div className="form-group"><label>Round Off (leave blank to auto-calculate)</label><input value={form.roundOff} onChange={e=>setForm(p=>({...p,roundOff:e.target.value.replace(/[^0-9.-]/g,'')}))} placeholder="auto" /></div>

            <div style={{ display:'flex', gap:'0.75rem', marginTop:'1rem' }}>
              <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':'Save Invoice'}</button>
              <button type="button" className="btn btn-dark" style={{ fontSize:'0.72rem' }} onClick={()=>{ setShowForm(false); resetForm(); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      {loading ? <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p> : (
        <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
          {invoices.length===0 && <p style={{ color:'#8a8278', textAlign:'center', padding:'4rem' }}>No invoices yet.</p>}
          {invoices.map((inv)=>{
            const isLegacy = (!inv.items || inv.items.length===0) && inv.itemSpec;
            const t = isLegacy ? null : computeInvoiceTotals(inv);
            return (
              <div key={inv._id} style={{ background:'#141414', border:'1px solid rgba(255,255,255,0.05)', borderRadius:'var(--radius)', padding:'1.25rem 1.5rem', display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:'0.75rem' }}>
                <div>
                  <div style={{ fontSize:'0.9rem', color:'#f8f5ef', fontWeight:500 }}>{isLegacy ? (inv.clientName||'—') : (inv.buyerName||'—')}</div>
                  <div style={{ fontSize:'0.68rem', color:'#8a8278', marginTop:'0.2rem' }}>
                    {isLegacy ? `${inv.itemSpec||''} · ${new Date(inv.createdAt).toLocaleDateString('en-IN')}` : `${inv.invoiceNo||'-'} · ${fmtInvDate(inv.invoiceDate)} · ${inv.items?.length||0} item(s)`}
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' }}>
                  {!isLegacy && <span style={{ color:'#F5A800', fontWeight:700, fontSize:'0.85rem' }}>₹ {fmtAmt(t.grandTotal)}</span>}
                  {!isLegacy && (
                    <>
                      <Link to={`/admin/invoice/${inv._id}/preview`} style={{ padding:'0.3rem 0.6rem', fontSize:'0.65rem', background:'rgba(245,168,0,0.08)', border:'1px solid rgba(245,168,0,0.2)', borderRadius:'var(--radius)', color:'#F5A800', cursor:'pointer' }}>👁 View</Link>
                      <button onClick={()=>downloadInvoicePDF(inv)} style={{ padding:'0.3rem 0.6rem', fontSize:'0.65rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'var(--radius)', color:'#ede8de', cursor:'pointer' }}>📄 PDF</button>
                      <button onClick={()=>downloadInvoiceWord(inv, "default", { settings, logoAsset, signatureAsset })} style={{ padding:'0.3rem 0.6rem', fontSize:'0.65rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'var(--radius)', color:'#ede8de', cursor:'pointer' }}>📝 Word</button>
                      <button
                        onClick={(e)=>{
                          if (shareMenuFor && shareMenuFor.id === inv._id) { setShareMenuFor(null); return; }
                          const r = e.currentTarget.getBoundingClientRect();
                          setShareMenuFor({ id: inv._id, top: r.bottom + 4, left: r.right - 150 });
                        }}
                        style={{ padding:'0.3rem 0.6rem', fontSize:'0.65rem', background:'rgba(255,255,255,0.05)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:'var(--radius)', color:'#ede8de', cursor:'pointer' }}
                      >📤 Share ▾</button>
                      {shareMenuFor && shareMenuFor.id === inv._id && createPortal(
                        <>
                          <div onClick={()=>setShareMenuFor(null)} style={{ position:'fixed', inset:0, zIndex:249 }} />
                          <div style={{ position:'fixed', top: shareMenuFor.top, left: shareMenuFor.left, background:'#1c1c1c', border:'1px solid rgba(245,168,0,0.2)', minWidth:'150px', zIndex:250, boxShadow:'0 8px 24px rgba(0,0,0,0.5)', borderRadius:'4px', overflow:'hidden' }}>
                            <button onClick={()=>shareInvoice(inv,'whatsapp')} style={{ display:'block', width:'100%', textAlign:'left', padding:'0.55rem 0.8rem', background:'none', border:'none', color:'#ede8de', fontSize:'0.72rem', cursor:'pointer' }}>📱 WhatsApp</button>
                            <button onClick={()=>shareInvoice(inv,'email')} style={{ display:'block', width:'100%', textAlign:'left', padding:'0.55rem 0.8rem', background:'none', border:'none', borderTop:'1px solid rgba(255,255,255,0.06)', color:'#ede8de', fontSize:'0.72rem', cursor:'pointer' }}>✉️ Email</button>
                          </div>
                        </>,
                        document.body
                      )}
                    </>
                  )}
                  <button onClick={()=>del(inv._id)} style={{ padding:'0.3rem 0.6rem', fontSize:'0.65rem', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.15)', borderRadius:'var(--radius)', color:'#f87171', cursor:'pointer' }}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <style>{`
      `}</style>
    </div>
  );
}

// ─── SETTINGS ──────────────────────────────────────────────────────────────
export function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ companyName:'', phone:'', altPhone:'', email:'', personalEmail:'', address:'', gstNo:'', adminName:'', bankAccountHolder:'', bankAccountNo:'', bankName:'', bankBranch:'', bankIFSC:'' });
  const [logo, setLogo] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [signature, setSignature] = useState('');
  const [sigFile, setSigFile] = useState(null);
  const [sigPreview, setSigPreview] = useState('');

  const load = () => api.get('/settings').then(r=>{
    const s = r.data || {};
    setForm({ companyName: s.companyName||'', phone: s.phone||'', altPhone: s.altPhone||'', email: s.email||'', personalEmail: s.personalEmail||'', address: s.address||'', gstNo: s.gstNo||'', adminName: s.adminName||'', bankAccountHolder: s.bankAccountHolder||'', bankAccountNo: s.bankAccountNo||'', bankName: s.bankName||'', bankBranch: s.bankBranch||'', bankIFSC: s.bankIFSC||'' });
    setLogo(s.logo||'');
    setSignature(s.signature||'');
  }).catch(()=>{}).finally(()=>setLoading(false));
  useEffect(()=>{load();},[]);

  const onFileChange = (e) => {
    const f = e.target.files[0];
    setFile(f);
    setPreview(f ? URL.createObjectURL(f) : '');
  };

  const onSigFileChange = (e) => {
    const f = e.target.files[0];
    setSigFile(f);
    setSigPreview(f ? URL.createObjectURL(f) : '');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!isValidOptionalPhone(form.phone)) { toast.error(PHONE_ERROR); return; }
    if (!isValidOptionalPhone(form.altPhone)) { toast.error(`Alternate mobile: ${PHONE_ERROR}`); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k,v])=>fd.append(k,v));
      if (file) fd.append('image', file);
      if (sigFile) fd.append('signature', sigFile);
      const r = await api.put('/settings', fd, { headers:{'Content-Type':'multipart/form-data'} });
      setLogo(r.data?.logo||logo);
      setSignature(r.data?.signature||signature);
      setFile(null); setPreview('');
      setSigFile(null); setSigPreview('');
      toast.success('Settings saved!');
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  if (loading) return <p style={{ color:'#8a8278', textAlign:'center', padding:'3rem' }}>Loading...</p>;

  return (
    <div>
      <AdminHeading eyebrow="Site" title="Settings" />
      <div style={{ background:'#141414', border:'1px solid rgba(245,168,0,0.15)', borderRadius:'var(--radius)', padding:'2rem', maxWidth:640 }}>
        <form onSubmit={handleSave}>
          <div className="grid-2">
            <div className="form-group">
              <label>Company Name</label>
              <input value={form.companyName} onChange={e=>setForm(p=>({...p,companyName:e.target.value}))} placeholder="Wedoo Interiors" />
            </div>
            <div className="form-group">
              <label>Admin Name</label>
              <input value={form.adminName} onChange={e=>setForm(p=>({...p,adminName:e.target.value}))} placeholder="Admin name" />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Phone</label>
              <input {...phoneInputProps} value={form.phone} onChange={e=>setForm(p=>({...p,phone:sanitizePhone(e.target.value)}))} placeholder="10-digit mobile number" />
            </div>
            <div className="form-group">
              <label>Alternate Mobile No</label>
              <input {...phoneInputProps} value={form.altPhone} onChange={e=>setForm(p=>({...p,altPhone:sanitizePhone(e.target.value)}))} placeholder="10-digit mobile number" />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={form.email} onChange={e=>setForm(p=>({...p,email:e.target.value}))} placeholder="info@wedoointeriors.com" />
            </div>
            <div className="form-group">
              <label>Personal Email Id</label>
              <input type="email" value={form.personalEmail} onChange={e=>setForm(p=>({...p,personalEmail:e.target.value}))} placeholder="you@example.com" />
            </div>
          </div>
          <div className="form-group">
            <label>Address</label>
            <textarea rows={3} value={form.address} onChange={e=>setForm(p=>({...p,address:e.target.value}))} placeholder="Company address" />
          </div>
          <div className="form-group">
            <label>GST No</label>
            <input value={form.gstNo} onChange={e=>setForm(p=>({...p,gstNo:e.target.value.toUpperCase()}))} placeholder="e.g. 22AAAAA0000A1Z5" />
          </div>
          <h4 style={{ color:'#F5A800', fontSize:'0.78rem', margin:'1.5rem 0 0.75rem', textTransform:'uppercase', letterSpacing:'0.04em' }}>Bank Details</h4>
          <div className="grid-2">
            <div className="form-group">
              <label>Account Holder's Name</label>
              <input value={form.bankAccountHolder} onChange={e=>setForm(p=>({...p,bankAccountHolder:e.target.value}))} placeholder="e.g. Wedoo Interiors" />
            </div>
            <div className="form-group">
              <label>Account Number</label>
              <input value={form.bankAccountNo} onChange={e=>setForm(p=>({...p,bankAccountNo:e.target.value.replace(/[^0-9]/g,'')}))} placeholder="e.g. 028705003257" />
            </div>
          </div>
          <div className="grid-2">
            <div className="form-group">
              <label>Bank Name</label>
              <input value={form.bankName} onChange={e=>setForm(p=>({...p,bankName:e.target.value}))} placeholder="e.g. ICICI Bank" />
            </div>
            <div className="form-group">
              <label>Branch</label>
              <input value={form.bankBranch} onChange={e=>setForm(p=>({...p,bankBranch:e.target.value}))} placeholder="e.g. Tumakuru" />
            </div>
          </div>
          <div className="form-group">
            <label>IFSC Code</label>
            <input value={form.bankIFSC} onChange={e=>setForm(p=>({...p,bankIFSC:e.target.value.toUpperCase()}))} placeholder="e.g. ICIC0000287" />
          </div>
          <div className="form-group">
            <label>Logo</label>
            <div style={{ display:'flex', alignItems:'center', gap:'1rem', flexWrap:'wrap' }}>
              {(preview || logo) && (
                <img src={preview || (logo?.startsWith('http')?logo:imgSrc(logo))} alt="Logo" style={{ height:56, background:'#0c0c0c', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'var(--radius)', padding:'0.4rem' }} />
              )}
              <input type="file" accept="image/*" onChange={onFileChange} />
            </div>
          </div>
          <div className="form-group">
            <label>Signature</label>
            <div style={{ display:'flex', alignItems:'center', gap:'1rem', flexWrap:'wrap' }}>
              {(sigPreview || signature) && (
                <img src={sigPreview || (signature?.startsWith('http')?signature:imgSrc(signature))} alt="Signature" style={{ height:56, background:'#0c0c0c', border:'1px solid rgba(255,255,255,0.1)', borderRadius:'var(--radius)', padding:'0.4rem' }} />
              )}
              <input type="file" accept="image/*" onChange={onSigFileChange} />
            </div>
          </div>
          <button type="submit" className="btn btn-gold" style={{ fontSize:'0.72rem' }} disabled={saving}>{saving?'Saving...':'Save Settings'}</button>
        </form>
      </div>
    </div>
  );
}
